import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { AlarmSettingsPanel } from '../context/AlarmPopup';
import { useTheme } from '../context/ThemeContext';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useLicense } from '../context/LicenseContext';

// ─── Types ──────────────────────────────────────────────────────────────────

interface Camera {
  id: number;
  name: string;
  cameraId: string;
  ip: string;
  site: string;
  location: string;
  type: 'Fixed' | 'PTZ' | 'Fisheye' | 'Multisensor';
  resolution: string;
  manufacturer: string;
  model: string;
  status: 'Online' | 'Offline' | 'Fault' | 'Disabled';
  addedDate: string;
  stream?: string;
}

interface IntegrationConfig {
  id: number;
  name: string;
  type: string;
  vendor: string;
  host: string;
  port: string;
  status: 'Connected' | 'Disconnected' | 'Error' | 'Pending';
  lastSync: string;
  description: string;
  icon: string;
}

interface Site {
  id: number;
  name: string;
  address: string;
  type: string;
  cameras: number;
  controllers: number;
  status: 'Active' | 'Inactive';
}

// ─── Seed data ───────────────────────────────────────────────────────────────

const SEED_CAMERAS: Camera[] = [
  { id:1,  name:'TH-ENTRY-01',  cameraId:'CAM-001', ip:'192.168.1.101', site:'Town Hall',         location:'Main Entry',        type:'Fixed',      resolution:'4K',   manufacturer:'Axis',     model:'P3245-V',    status:'Online',  addedDate:'2024-06-01' },
  { id:2,  name:'TH-ENTRY-02',  cameraId:'CAM-002', ip:'192.168.1.102', site:'Town Hall',         location:'Side Entry',        type:'Fixed',      resolution:'4K',   manufacturer:'Axis',     model:'P3245-V',    status:'Online',  addedDate:'2024-06-01' },
  { id:3,  name:'TH-LOBBY-01',  cameraId:'CAM-003', ip:'192.168.1.103', site:'Town Hall',         location:'Lobby',             type:'PTZ',        resolution:'4K',   manufacturer:'Axis',     model:'Q6135-LE',   status:'Online',  addedDate:'2024-06-01' },
  { id:4,  name:'TH-CAR-01',    cameraId:'CAM-004', ip:'192.168.1.110', site:'Town Hall',         location:'Car Park L1',       type:'Fixed',      resolution:'1080p',manufacturer:'Milestone', model:'XP-CAM',    status:'Online',  addedDate:'2024-07-10' },
  { id:5,  name:'CC-ENTRY-01',  cameraId:'CAM-010', ip:'192.168.2.101', site:'Civic Centre',      location:'Main Entry',        type:'Fixed',      resolution:'4K',   manufacturer:'Axis',     model:'P3245-V',    status:'Online',  addedDate:'2024-06-15' },
  { id:6,  name:'CC-ATRIUM-01', cameraId:'CAM-011', ip:'192.168.2.102', site:'Civic Centre',      location:'Atrium',            type:'Fisheye',    resolution:'4K',   manufacturer:'Axis',     model:'M3106',      status:'Online',  addedDate:'2024-06-15' },
  { id:7,  name:'AQ-ENTRY-01',  cameraId:'CAM-020', ip:'192.168.3.101', site:'Aquatic Centre',    location:'Main Entry',        type:'Fixed',      resolution:'1080p',manufacturer:'Hanwha',   model:'QNV-8080R',  status:'Online',  addedDate:'2024-08-01' },
  { id:8,  name:'AQ-POOL-01',   cameraId:'CAM-021', ip:'192.168.3.102', site:'Aquatic Centre',    location:'Pool Deck',         type:'Multisensor','resolution':'4K',  manufacturer:'Axis',     model:'P3807-PVE',  status:'Fault',   addedDate:'2024-08-01' },
  { id:9,  name:'DEP-ENTRY-01', cameraId:'CAM-030', ip:'192.168.4.101', site:'Council Depot',     location:'Entry Gate',        type:'Fixed',      resolution:'4K',   manufacturer:'Axis',     model:'P1448-LE',   status:'Online',  addedDate:'2024-09-10' },
  { id:10, name:'PS-ENTRY-01',  cameraId:'CAM-040', ip:'192.168.5.101', site:'Parramatta Square', location:'Main Entry',        type:'PTZ',        resolution:'4K',   manufacturer:'Axis',     model:'Q6135-LE',   status:'Online',  addedDate:'2024-10-01' },
  { id:11, name:'PS-PLAZA-01',  cameraId:'CAM-041', ip:'192.168.5.102', site:'Parramatta Square', location:'Entry Plaza',       type:'Fixed',      resolution:'4K',   manufacturer:'Axis',     model:'P3245-V',    status:'Online',  addedDate:'2024-10-01' },
  { id:12, name:'RW-PATH-01',   cameraId:'CAM-050', ip:'192.168.6.101', site:'River Walk',        location:'River Path North',  type:'Fixed',      resolution:'1080p',manufacturer:'Hanwha',   model:'XNO-8080R',  status:'Offline', addedDate:'2024-11-15' },
];

const SEED_INTEGRATIONS: IntegrationConfig[] = [
  { id:1, name:'ArmorIP',             type:'Alarm Monitoring',vendor:'ArmorIP',          host:'134.199.173.91', port:'5000', status:'Connected', lastSync:'Just now',    description:'Patriot alarm receiver — live alarm feed, SLA monitoring, audit log. All Parramatta Council alarm zones active.', icon:'🛡️' },
  { id:2, name:'Milestone XProtect',  type:'VMS',             vendor:'Milestone Systems',host:'10.0.0.50',  port:'80',   status:'Pending',      lastSync:'Never',       description:'VMS integration pending on-site configuration. Cameras currently managed via manual registry.',                    icon:'📹' },
  { id:3, name:'Protege GX',          type:'Access Control',  vendor:'ICT',              host:'10.0.0.60',  port:'443',  status:'Connected',    lastSync:'4 min ago',   description:'Access control — 5 controllers, 24 doors, 1,240 cardholders',          icon:'🔐' },
  { id:4, name:'Inner Range Integriti',type:'Alarm Panel',    vendor:'Inner Range',      host:'10.0.0.70',  port:'8080', status:'Disconnected', lastSync:'3 hours ago', description:'Alarm management system — pending reconfiguration after site migration', icon:'🚨' },
  { id:5, name:'Genetec Security Centre',type:'PSIM',         vendor:'Genetec',          host:'',           port:'',     status:'Pending',      lastSync:'Never',       description:'Physical security information management — pending licence provisioning', icon:'🔒' },
  { id:6, name:'SCOOT Traffic',        type:'Traffic',        vendor:'Siemens',          host:'10.0.0.80',  port:'9090', status:'Connected',    lastSync:'10 min ago',  description:'Smart traffic management API — Parramatta CBD signal network',          icon:'🚦' },
];

const SEED_SITES: Site[] = [
  { id:1, name:'Town Hall',          address:'182 Church St, Parramatta NSW 2150',     type:'Council Office',  cameras:4,  controllers:2, status:'Active' },
  { id:2, name:'Civic Centre',       address:'Darcy St, Parramatta NSW 2150',          type:'Council Office',  cameras:2,  controllers:1, status:'Active' },
  { id:3, name:'Aquatic Centre',     address:'666 Macquarie St, Parramatta NSW 2150',  type:'Recreation',      cameras:2,  controllers:1, status:'Active' },
  { id:4, name:'Council Depot',      address:'47 Macarthur St, Parramatta NSW 2150',   type:'Operations',      cameras:1,  controllers:1, status:'Active' },
  { id:5, name:'Parramatta Square',  address:'12 Darcy St, Parramatta NSW 2150',       type:'Commercial',      cameras:2,  controllers:1, status:'Active' },
  { id:6, name:'River Walk',         address:'River Rd, Parramatta NSW 2150',          type:'Public Space',    cameras:1,  controllers:0, status:'Active' },
];

const SITES_LIST  = SEED_SITES.map(s => s.name);
const CAM_TYPES   = ['Fixed', 'PTZ', 'Fisheye', 'Multisensor'];
const RESOLUTIONS = ['4K', '1080p', '720p', '5MP', '8MP'];
const MANUFACTURERS = ['Axis', 'Hanwha', 'Hikvision', 'Dahua', 'Bosch', 'Pelco', 'Milestone', 'Other'];
const INT_TYPES   = ['VMS', 'Access Control', 'Alarm Panel', 'PSIM', 'Traffic', 'HR System', 'Building Management', 'Other'];
const INT_VENDORS = ['Milestone Systems', 'ICT', 'Inner Range', 'Genetec', 'Gallagher', 'Lenel', 'CCURE', 'Other'];

const STATUS_COLOR: Record<string, string> = {
  Online:'#22c55e', Offline:'#ef4444', Fault:'#f59e0b', Disabled:'#64748b',
  Connected:'#22c55e', Disconnected:'#ef4444', Error:'#ef4444', Pending:'#f59e0b',
  Active:'#22c55e', Inactive:'#64748b',
};

// ─── Add Camera Modal ────────────────────────────────────────────────────────

const BLANK_CAM = { name:'', cameraId:'', ip:'', site:SITES_LIST[0], location:'', type:'Fixed' as const, resolution:'4K', manufacturer:'Axis', model:'', stream:'', status:'Online' as const };

function AddCameraModal({ onSave, onClose }: { onSave: (data: typeof BLANK_CAM) => void; onClose: () => void }) {
  const [form, setForm] = useState({ ...BLANK_CAM });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  function validate() {
    const e: Record<string, string> = {};
    if (!form.name.trim())         e.name         = 'Camera name is required';
    if (!form.cameraId.trim())     e.cameraId     = 'Camera ID is required';
    if (!form.ip.trim())           e.ip           = 'IP address is required';
    if (!form.location.trim())     e.location     = 'Location is required';
    if (!form.model.trim())        e.model        = 'Model is required';
    return e;
  }

  async function handleSubmit() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    await new Promise(r => setTimeout(r, 900));
    setSaving(false);
    setSaved(true);
    onSave(form);
    setTimeout(onClose, 1200);
  }

  const inp = (label: string, key: string, placeholder = '', half = false): React.ReactNode => (
    <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
      <label style={{ display:'block', fontSize:'11px', color:'#64748b', marginBottom:'5px', fontWeight:500 }}>{label}</label>
      <input
        value={(form as any)[key]}
        onChange={e => set(key, e.target.value)}
        placeholder={placeholder}
        style={{
          width:'100%', background:'rgba(255,255,255,0.05)', border:`1px solid ${errors[key]?'#ef4444':'rgba(255,255,255,0.1)'}`,
          borderRadius:'7px', padding:'8px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none', boxSizing:'border-box',
        }}
      />
      {errors[key] && <div style={{ fontSize:'10px', color:'#ef4444', marginTop:'3px' }}>{errors[key]}</div>}
    </div>
  );

  const sel = (label: string, key: string, opts: string[], half = false): React.ReactNode => (
    <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
      <label style={{ display:'block', fontSize:'11px', color:'#64748b', marginBottom:'5px', fontWeight:500 }}>{label}</label>
      <select
        value={(form as any)[key]}
        onChange={e => set(key, e.target.value)}
        style={{ width:'100%', background:'#1e293b', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'8px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none' }}
      >
        {opts.map(o => <option key={o} style={{ background:'#1e293b' }}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }}>
      <div style={{ background:'#0d1321', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'16px', width:'560px', maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto' }}>
        {/* Header */}
        <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h2 style={{ margin:0, fontSize:'16px', fontWeight:700, color:'#e2e8f0' }}>Add Camera</h2>
            <p style={{ margin:'3px 0 0', fontSize:'11px', color:'#475569' }}>Register a new camera to the platform</p>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'8px', color:'#64748b', width:'32px', height:'32px', fontSize:'16px', cursor:'pointer' }}>✕</button>
        </div>
        {/* Form */}
        <div style={{ padding:'20px 24px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
          {inp('Camera Name *', 'name', 'e.g. TH-ENTRY-01')}
          {inp('Camera ID *', 'cameraId', 'e.g. CAM-001', true)}
          {inp('IP Address *', 'ip', 'e.g. 192.168.1.101', true)}
          {sel('Site', 'site', SITES_LIST, true)}
          {inp('Location *', 'location', 'e.g. Main Entry', true)}
          {sel('Camera Type', 'type', CAM_TYPES, true)}
          {sel('Resolution', 'resolution', RESOLUTIONS, true)}
          {sel('Manufacturer', 'manufacturer', MANUFACTURERS, true)}
          {inp('Model *', 'model', 'e.g. P3245-V')}
          {inp('Stream URL (optional)', 'stream', 'rtsp://...')}
        </div>
        {/* Footer */}
        <div style={{ padding:'14px 24px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'flex-end', gap:'10px' }}>
          <button onClick={onClose} style={{ padding:'8px 20px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#64748b', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving || saved} style={{
            padding:'8px 24px', borderRadius:'8px', border:'none', fontSize:'12px', fontWeight:700, cursor:saving||saved?'default':'pointer',
            background: saved ? 'rgba(34,197,94,0.2)' : 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
            color: saved ? '#22c55e' : 'white', transition:'all 0.2s',
          }}>
            {saved ? '✓ Camera Added!' : saving ? 'Adding...' : 'Add Camera'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add Integration Modal ────────────────────────────────────────────────────

const BLANK_INT = { name:'', type:INT_TYPES[0], vendor:INT_VENDORS[0], host:'', port:'', description:'' };

function AddIntegrationModal({ onSave, onClose }: { onSave: (data: typeof BLANK_INT) => void; onClose: () => void }) {
  const [form, setForm] = useState({ ...BLANK_INT });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<null | boolean>(null);

  const set = (k: string, v: string) => { setForm(f => ({ ...f, [k]: v })); setTestResult(null); };

  function validate() {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Integration name is required';
    if (!form.host.trim()) e.host = 'Host/IP is required';
    return e;
  }

  async function handleTest() {
    setTesting(true); setTestResult(null);
    await new Promise(r => setTimeout(r, 1400));
    setTesting(false);
    setTestResult(form.host.length > 3);
  }

  async function handleSubmit() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    await new Promise(r => setTimeout(r, 900));
    setSaving(false); setSaved(true);
    onSave(form);
    setTimeout(onClose, 1200);
  }

  const inp = (label: string, key: string, placeholder = '', half = false) => (
    <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
      <label style={{ display:'block', fontSize:'11px', color:'#64748b', marginBottom:'5px', fontWeight:500 }}>{label}</label>
      <input value={(form as any)[key]} onChange={e => set(key, e.target.value)} placeholder={placeholder}
        style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:`1px solid ${(errors as any)[key]?'#ef4444':'rgba(255,255,255,0.1)'}`, borderRadius:'7px', padding:'8px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none', boxSizing:'border-box' as const }} />
      {(errors as any)[key] && <div style={{ fontSize:'10px', color:'#ef4444', marginTop:'3px' }}>{(errors as any)[key]}</div>}
    </div>
  );

  const sel = (label: string, key: string, opts: string[], half = false) => (
    <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
      <label style={{ display:'block', fontSize:'11px', color:'#64748b', marginBottom:'5px', fontWeight:500 }}>{label}</label>
      <select value={(form as any)[key]} onChange={e => set(key, e.target.value)}
        style={{ width:'100%', background:'#1e293b', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'8px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none' }}>
        {opts.map(o => <option key={o} style={{ background:'#1e293b' }}>{o}</option>)}
      </select>
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(4px)' }}>
      <div style={{ background:'#0d1321', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'16px', width:'540px', maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <h2 style={{ margin:0, fontSize:'16px', fontWeight:700, color:'#e2e8f0' }}>Add Integration</h2>
            <p style={{ margin:'3px 0 0', fontSize:'11px', color:'#475569' }}>Connect a new system or service</p>
          </div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'8px', color:'#64748b', width:'32px', height:'32px', fontSize:'16px', cursor:'pointer' }}>✕</button>
        </div>
        <div style={{ padding:'20px 24px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
          {inp('Integration Name *', 'name', 'e.g. Milestone XProtect')}
          {sel('Type', 'type', INT_TYPES, true)}
          {sel('Vendor', 'vendor', INT_VENDORS, true)}
          {inp('Host / IP Address *', 'host', 'e.g. 10.0.0.50', true)}
          {inp('Port', 'port', 'e.g. 80', true)}
          <div style={{ gridColumn:'span 2' }}>
            <label style={{ display:'block', fontSize:'11px', color:'#64748b', marginBottom:'5px', fontWeight:500 }}>Description</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description of this integration..." rows={2}
              style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'8px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none', boxSizing:'border-box', resize:'none' as const }} />
          </div>
          {/* Test connection */}
          <div style={{ gridColumn:'span 2', padding:'12px', background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'8px', display:'flex', alignItems:'center', gap:'12px' }}>
            <button onClick={handleTest} disabled={testing || !form.host} style={{ padding:'6px 16px', borderRadius:'7px', border:'none', background:'rgba(59,130,246,0.15)', color:'#3b82f6', fontSize:'11px', fontWeight:600, cursor: form.host ? 'pointer' : 'default', whiteSpace:'nowrap' as const }}>
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
            {testResult === true  && <span style={{ fontSize:'11px', color:'#22c55e' }}>Connection successful</span>}
            {testResult === false && <span style={{ fontSize:'11px', color:'#ef4444' }}>Connection failed — check host/port</span>}
            {testResult === null  && !testing && <span style={{ fontSize:'11px', color:'#475569' }}>Test the connection before saving</span>}
          </div>
        </div>
        <div style={{ padding:'14px 24px 20px', borderTop:'1px solid rgba(255,255,255,0.06)', display:'flex', justifyContent:'flex-end', gap:'10px' }}>
          <button onClick={onClose} style={{ padding:'8px 20px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#64748b', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>Cancel</button>
          <button onClick={handleSubmit} disabled={saving||saved} style={{
            padding:'8px 24px', borderRadius:'8px', border:'none', fontSize:'12px', fontWeight:700, cursor:saving||saved?'default':'pointer',
            background: saved ? 'rgba(34,197,94,0.2)' : 'linear-gradient(135deg,#3b82f6,#1d4ed8)',
            color: saved ? '#22c55e' : 'white', transition:'all 0.2s',
          }}>
            {saved ? '✓ Integration Added!' : saving ? 'Saving...' : 'Save Integration'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Active Sessions Tab ──────────────────────────────────────────────────────

interface SessionInfo {
  jti: string;
  email: string;
  ip: string;
  user_agent: string;
  created_at: string;
  last_used: string;
  is_current: boolean;
}

function SessionsTab() {
  const [sessions, setSessions] = React.useState<SessionInfo[]>([]);
  const [loading, setLoading]   = React.useState(true);
  const [revoking, setRevoking] = React.useState<string | null>(null);
  const tok = localStorage.getItem('lemai_token') || '';

  function load() {
    setLoading(true);
    fetch('/api/auth/sessions', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.json())
      .then(d => setSessions(d.sessions || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  React.useEffect(() => { load(); }, []);

  function revoke(jti: string) {
    setRevoking(jti);
    fetch(`/api/auth/sessions/${jti}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${tok}` },
    })
      .then(() => setSessions(s => s.filter(x => x.jti !== jti)))
      .catch(() => {})
      .finally(() => setRevoking(null));
  }

  function browserName(ua: string): string {
    if (ua.includes('Edg')) return 'Edge';
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    return 'Browser';
  }

  function osPart(ua: string): string {
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    return 'Unknown OS';
  }

  function fmtDate(iso: string) {
    try {
      return new Date(iso).toLocaleString('en-AU', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
    } catch { return iso; }
  }

  return (
    <div style={{ padding:'20px', display:'flex', flexDirection:'column', gap:'20px' }}>
      <div style={{ background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.2)', borderRadius:'12px', padding:'20px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
          <div>
            <div style={{ fontSize:'14px', fontWeight:700, color:'#e2e8f0', marginBottom:'4px' }}>Active Sessions</div>
            <div style={{ fontSize:'12px', color:'#64748b' }}>
              All devices currently logged into your account. Revoke any session you don't recognise.
            </div>
          </div>
          <button onClick={load} style={{ padding:'6px 14px', borderRadius:'8px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', color:'#94a3b8', cursor:'pointer', fontSize:'12px' }}>
            Refresh
          </button>
        </div>
        {loading ? (
          <div style={{ textAlign:'center', padding:'20px', color:'#475569', fontSize:'12px' }}>Loading sessions...</div>
        ) : sessions.length === 0 ? (
          <div style={{ textAlign:'center', padding:'20px', color:'#475569', fontSize:'12px' }}>No active sessions found.</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            {sessions.map(s => (
              <div key={s.jti} style={{
                display:'flex', alignItems:'center', gap:'12px',
                padding:'12px 16px', borderRadius:'10px',
                background: s.is_current ? 'rgba(34,197,94,0.06)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${s.is_current ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.07)'}`,
              }}>
                <div style={{ fontSize:'22px', flexShrink:0 }}>
                  {osPart(s.user_agent) === 'iOS' || osPart(s.user_agent) === 'Android' ? '📱' : '🖥'}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'3px' }}>
                    <span style={{ fontSize:'13px', fontWeight:600, color:'#e2e8f0' }}>
                      {browserName(s.user_agent)} on {osPart(s.user_agent)}
                    </span>
                    {s.is_current && (
                      <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 8px', borderRadius:'6px', background:'rgba(34,197,94,0.15)', color:'#22c55e' }}>
                        This device
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize:'11px', color:'#64748b' }}>
                    IP: {s.ip} · Last active: {fmtDate(s.last_used)} · Signed in: {fmtDate(s.created_at)}
                  </div>
                </div>
                {!s.is_current && (
                  <button
                    onClick={() => revoke(s.jti)}
                    disabled={revoking === s.jti}
                    style={{
                      padding:'5px 12px', borderRadius:'7px', fontSize:'11px', fontWeight:700,
                      background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)',
                      color:'#ef4444', cursor:'pointer', flexShrink:0, transition:'all 0.15s',
                    }}
                  >
                    {revoking === s.jti ? 'Revoking...' : 'Revoke'}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ background:'rgba(255,165,0,0.05)', border:'1px solid rgba(255,165,0,0.15)', borderRadius:'10px', padding:'14px 16px', fontSize:'11px', color:'#94a3b8', lineHeight:1.6 }}>
        <strong style={{ color:'#f59e0b' }}>Security tip:</strong> If you see a session you don't recognise, revoke it immediately and change your password. Sessions automatically expire after 1 hour of access token inactivity but your refresh token keeps you logged in for up to 30 days.
      </div>
    </div>
  );
}

// ─── Security & MFA Tab ───────────────────────────────────────────────────────

function SecurityMFATab() {
  const { toast } = useToast();
  const { user } = useAuth();
  const tok = localStorage.getItem('lemai_token') || '';

  // Initialise from real user.totp_enabled field
  const [mfaEnabled, setMfaEnabled] = React.useState<boolean>(!!(user as any)?.totp_enabled);
  const [showSetup, setShowSetup] = React.useState(false);
  const [setupStep, setSetupStep] = React.useState<'scan'|'verify'|'done'>('scan');
  const [qrCode, setQrCode] = React.useState('');      // data URI from backend
  const [secret, setSecret] = React.useState('');       // plain TOTP secret for manual entry
  const [verifyCode, setVerifyCode] = React.useState('');
  const [verifyError, setVerifyError] = React.useState('');
  const [backupCodes, setBackupCodes] = React.useState<string[]>([]);
  const [setupLoading, setSetupLoading] = React.useState(false);
  // Disable flow
  const [showDisable, setShowDisable] = React.useState(false);
  const [disableCode, setDisableCode] = React.useState('');
  const [disableError, setDisableError] = React.useState('');
  const [disableLoading, setDisableLoading] = React.useState(false);
  // Change password
  const [cpCurrent, setCpCurrent] = React.useState('');
  const [cpNew, setCpNew] = React.useState('');
  const [cpConfirm, setCpConfirm] = React.useState('');
  const [cpLoading, setCpLoading] = React.useState(false);
  const [cpError, setCpError] = React.useState('');
  const [cpSuccess, setCpSuccess] = React.useState(false);
  // Policy
  const [sessionTimeout, setSessionTimeout] = React.useState('60');
  const [ipWhitelist, setIpWhitelist] = React.useState('');
  const [pwMinLen, setPwMinLen] = React.useState('12');
  const [pwExpiry, setPwExpiry] = React.useState('90');
  const [saved, setSaved] = React.useState(false);

  async function handleChangePassword() {
    setCpError('');
    setCpSuccess(false);
    if (!cpCurrent || !cpNew || !cpConfirm) { setCpError('All fields are required.'); return; }
    if (cpNew.length < 8) { setCpError('New password must be at least 8 characters.'); return; }
    if (cpNew !== cpConfirm) { setCpError('New passwords do not match.'); return; }
    setCpLoading(true);
    try {
      const r = await fetch('/api/auth/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ current_password: cpCurrent, new_password: cpNew }),
      });
      const d = await r.json();
      if (r.ok) {
        setCpSuccess(true);
        setCpCurrent(''); setCpNew(''); setCpConfirm('');
        toast('Password changed successfully', 'success');
      } else {
        setCpError(d.error || 'Failed to change password.');
      }
    } catch {
      setCpError('Network error — try again.');
    } finally {
      setCpLoading(false);
    }
  }

  function generateBackupCodes(): string[] {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    return Array.from({ length: 8 }, () =>
      'LEMAI-' + Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
    );
  }

  async function startSetup() {
    setSetupLoading(true);
    setSetupStep('scan');
    try {
      const r = await fetch('/api/auth/setup-2fa', {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok}` },
      });
      const d = await r.json();
      if (r.ok) {
        setQrCode(d.qr_code);
        setSecret(d.secret);
        setShowSetup(true);
      } else {
        toast('Failed to start MFA setup', 'error');
      }
    } catch {
      toast('Network error — try again', 'error');
    } finally {
      setSetupLoading(false);
    }
  }

  async function handleVerify() {
    if (verifyCode.length !== 6 || !/^\d+$/.test(verifyCode)) {
      setVerifyError('Enter a valid 6-digit code from your authenticator app');
      return;
    }
    setVerifyError('');
    try {
      const r = await fetch('/api/auth/verify-2fa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ token: verifyCode }),
      });
      if (r.ok) {
        const codes = generateBackupCodes();
        setBackupCodes(codes);
        setSetupStep('done');
        setTimeout(() => {
          setMfaEnabled(true);
          setShowSetup(false);
          setSetupStep('scan');
          setVerifyCode('');
        }, 2500);
      } else {
        const d = await r.json();
        setVerifyError(d.error || 'Invalid code — check your authenticator app and try again');
      }
    } catch {
      setVerifyError('Network error — try again');
    }
  }

  async function handleDisable() {
    if (disableCode.length !== 6 || !/^\d+$/.test(disableCode)) {
      setDisableError('Enter the 6-digit code from your authenticator app');
      return;
    }
    setDisableLoading(true);
    setDisableError('');
    try {
      const r = await fetch('/api/auth/disable-2fa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ token: disableCode }),
      });
      if (r.ok) {
        setMfaEnabled(false);
        setShowDisable(false);
        setDisableCode('');
        setBackupCodes([]);
        toast('MFA disabled', 'info');
      } else {
        const d = await r.json();
        setDisableError(d.error || 'Invalid code');
      }
    } catch {
      setDisableError('Network error — try again');
    } finally {
      setDisableLoading(false);
    }
  }

  return (
    <div style={{ padding:'20px', maxWidth:'640px' }}>
      {/* MFA Section */}
      <div style={{ marginBottom:'24px' }}>
        <div style={{ fontWeight:700, fontSize:'14px', color:'white', marginBottom:'4px' }}>Multi-Factor Authentication (MFA)</div>
        <div style={{ fontSize:'12px', color:'#64748b', marginBottom:'16px' }}>Add an extra layer of security to your account. Required for Admin and Supervisor roles in enterprise deployments.</div>

        <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', padding:'16px', marginBottom:'12px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'12px' }}>
            <div>
              <div style={{ fontSize:'13px', fontWeight:600, color:'white' }}>Authenticator App (TOTP)</div>
              <div style={{ fontSize:'11px', color:'#64748b' }}>Google Authenticator, Authy, 1Password, etc.</div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              {mfaEnabled && <span style={{ fontSize:'11px', color:'#22c55e', fontWeight:600 }}>✓ Enabled</span>}
              {!mfaEnabled && !showSetup && (
                <button onClick={startSetup} disabled={setupLoading}
                  style={{ padding:'6px 16px', borderRadius:'8px', border:'none', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontSize:'12px', fontWeight:700, cursor:'pointer', opacity: setupLoading ? 0.6 : 1 }}>
                  {setupLoading ? 'Loading…' : 'Set Up MFA'}
                </button>
              )}
              {mfaEnabled && !showDisable && (
                <button onClick={() => setShowDisable(true)}
                  style={{ padding:'6px 16px', borderRadius:'8px', border:'none', background:'rgba(239,68,68,0.1)', color:'#ef4444', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>
                  Disable MFA
                </button>
              )}
            </div>
          </div>

          {/* Setup flow */}
          {showSetup && (
            <div style={{ border:'1px solid rgba(59,130,246,0.2)', borderRadius:'10px', padding:'16px', background:'rgba(59,130,246,0.04)' }}>
              {setupStep === 'scan' && (
                <>
                  <div style={{ fontSize:'12px', fontWeight:600, color:'#93c5fd', marginBottom:'12px' }}>Step 1 — Scan QR Code</div>
                  <div style={{ display:'flex', gap:'16px', alignItems:'flex-start' }}>
                    <img src={qrCode} alt="MFA QR Code" style={{ width:'120px', height:'120px', borderRadius:'8px', background:'white', padding:'4px', flexShrink:0 }} />
                    <div>
                      <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'8px', lineHeight:'1.5' }}>Open your authenticator app and scan this QR code, or enter the key manually:</div>
                      <div style={{ fontSize:'10px', fontFamily:'monospace', background:'rgba(0,0,0,0.3)', padding:'6px 10px', borderRadius:'6px', color:'#93c5fd', letterSpacing:'1px', marginBottom:'12px', wordBreak:'break-all' }}>{secret}</div>
                      <div style={{ display:'flex', gap:'8px' }}>
                        <button onClick={() => setSetupStep('verify')} style={{ padding:'7px 18px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:'8px', color:'white', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>Next →</button>
                        <button onClick={() => { setShowSetup(false); setSetupStep('scan'); }} style={{ padding:'7px 12px', background:'transparent', border:'none', color:'#64748b', fontSize:'12px', cursor:'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  </div>
                </>
              )}
              {setupStep === 'verify' && (
                <>
                  <div style={{ fontSize:'12px', fontWeight:600, color:'#93c5fd', marginBottom:'12px' }}>Step 2 — Verify Code</div>
                  <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'10px' }}>Enter the 6-digit code shown in your authenticator app:</div>
                  <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
                    <input value={verifyCode} onChange={e => { setVerifyCode(e.target.value.slice(0,6)); setVerifyError(''); }} placeholder="000000" maxLength={6}
                      style={{ width:'120px', padding:'10px', background:'rgba(255,255,255,0.07)', border:`1px solid ${verifyError?'#ef4444':'rgba(255,255,255,0.1)'}`, borderRadius:'8px', color:'#e2e8f0', fontSize:'18px', fontFamily:'monospace', textAlign:'center', letterSpacing:'4px', outline:'none' }} />
                    <button onClick={handleVerify} style={{ padding:'10px 18px', background:'linear-gradient(135deg,#22c55e,#16a34a)', border:'none', borderRadius:'8px', color:'white', fontSize:'13px', fontWeight:700, cursor:'pointer' }}>Verify &amp; Enable</button>
                    <button onClick={() => setSetupStep('scan')} style={{ padding:'10px', background:'transparent', border:'none', color:'#64748b', fontSize:'12px', cursor:'pointer' }}>← Back</button>
                  </div>
                  {verifyError && <div style={{ fontSize:'11px', color:'#ef4444', marginTop:'6px' }}>{verifyError}</div>}
                </>
              )}
              {setupStep === 'done' && (
                <div style={{ textAlign:'center', padding:'10px' }}>
                  <div style={{ fontSize:'32px', marginBottom:'8px' }}>✅</div>
                  <div style={{ color:'#22c55e', fontWeight:700, fontSize:'14px', marginBottom:'8px' }}>MFA Enabled Successfully</div>
                  {backupCodes.length > 0 && (
                    <>
                      <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'10px' }}>Save these backup codes — each can be used once if you lose your authenticator.</div>
                      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'4px', textAlign:'left' }}>
                        {backupCodes.map(c => <div key={c} style={{ fontFamily:'monospace', fontSize:'11px', color:'#93c5fd', background:'rgba(59,130,246,0.08)', padding:'4px 6px', borderRadius:'5px', textAlign:'center' }}>{c}</div>)}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Disable flow */}
          {showDisable && (
            <div style={{ border:'1px solid rgba(239,68,68,0.2)', borderRadius:'10px', padding:'16px', background:'rgba(239,68,68,0.04)', marginTop:'12px' }}>
              <div style={{ fontSize:'12px', fontWeight:600, color:'#f87171', marginBottom:'8px' }}>Disable MFA — Confirm with Authenticator Code</div>
              <div style={{ fontSize:'11px', color:'#94a3b8', marginBottom:'10px' }}>Enter your current 6-digit authenticator code to confirm:</div>
              <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
                <input value={disableCode} onChange={e => { setDisableCode(e.target.value.slice(0,6)); setDisableError(''); }} placeholder="000000" maxLength={6}
                  style={{ width:'120px', padding:'10px', background:'rgba(255,255,255,0.07)', border:`1px solid ${disableError?'#ef4444':'rgba(255,255,255,0.1)'}`, borderRadius:'8px', color:'#e2e8f0', fontSize:'18px', fontFamily:'monospace', textAlign:'center', letterSpacing:'4px', outline:'none' }} />
                <button onClick={handleDisable} disabled={disableLoading}
                  style={{ padding:'10px 18px', background:'rgba(239,68,68,0.2)', border:'1px solid rgba(239,68,68,0.4)', borderRadius:'8px', color:'#f87171', fontSize:'13px', fontWeight:700, cursor:'pointer', opacity: disableLoading ? 0.6 : 1 }}>
                  {disableLoading ? 'Disabling…' : 'Confirm Disable'}
                </button>
                <button onClick={() => { setShowDisable(false); setDisableCode(''); setDisableError(''); }} style={{ padding:'10px', background:'transparent', border:'none', color:'#64748b', fontSize:'12px', cursor:'pointer' }}>Cancel</button>
              </div>
              {disableError && <div style={{ fontSize:'11px', color:'#ef4444', marginTop:'6px' }}>{disableError}</div>}
            </div>
          )}

          {mfaEnabled && !showDisable && backupCodes.length > 0 && (
            <div style={{ marginTop:'12px', padding:'12px', background:'rgba(34,197,94,0.05)', border:'1px solid rgba(34,197,94,0.15)', borderRadius:'10px' }}>
              <div style={{ fontSize:'12px', fontWeight:600, color:'#22c55e', marginBottom:'8px' }}>Backup Recovery Codes</div>
              <div style={{ fontSize:'11px', color:'#64748b', marginBottom:'8px' }}>Store these codes safely — each can be used once if you lose access to your authenticator.</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'4px' }}>
                {backupCodes.map(c => <div key={c} style={{ fontFamily:'monospace', fontSize:'11px', color:'#93c5fd', background:'rgba(59,130,246,0.08)', padding:'4px 6px', borderRadius:'5px', textAlign:'center' }}>{c}</div>)}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Change Password */}
      <div style={{ marginBottom:'24px' }}>
        <div style={{ fontWeight:700, fontSize:'14px', color:'white', marginBottom:'4px' }}>Change Password</div>
        <div style={{ fontSize:'12px', color:'#64748b', marginBottom:'14px' }}>Update your account password. You will remain logged in after changing.</div>
        <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', padding:'16px' }}>
          {cpSuccess && (
            <div style={{ padding:'10px 14px', background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:'8px', color:'#22c55e', fontSize:'12px', fontWeight:600, marginBottom:'12px' }}>
              ✓ Password changed successfully.
            </div>
          )}
          <div style={{ display:'grid', gap:'12px' }}>
            <div>
              <label style={{ fontSize:'12px', fontWeight:600, color:'#94a3b8', display:'block', marginBottom:'5px' }}>Current Password</label>
              <input type="password" value={cpCurrent} onChange={e => { setCpCurrent(e.target.value); setCpError(''); setCpSuccess(false); }} placeholder="Enter current password"
                style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', padding:'9px 12px', color:'#e2e8f0', fontSize:'13px', outline:'none', boxSizing:'border-box' as const }} />
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
              <div>
                <label style={{ fontSize:'12px', fontWeight:600, color:'#94a3b8', display:'block', marginBottom:'5px' }}>New Password</label>
                <input type="password" value={cpNew} onChange={e => { setCpNew(e.target.value); setCpError(''); setCpSuccess(false); }} placeholder="Min 8 characters"
                  style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', padding:'9px 12px', color:'#e2e8f0', fontSize:'13px', outline:'none', boxSizing:'border-box' as const }} />
              </div>
              <div>
                <label style={{ fontSize:'12px', fontWeight:600, color:'#94a3b8', display:'block', marginBottom:'5px' }}>Confirm New Password</label>
                <input type="password" value={cpConfirm} onChange={e => { setCpConfirm(e.target.value); setCpError(''); setCpSuccess(false); }} placeholder="Re-enter new password"
                  style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', padding:'9px 12px', color:'#e2e8f0', fontSize:'13px', outline:'none', boxSizing:'border-box' as const }} />
              </div>
            </div>
            {cpError && <div style={{ fontSize:'11px', color:'#ef4444' }}>{cpError}</div>}
            <div>
              <button onClick={handleChangePassword} disabled={cpLoading}
                style={{ padding:'9px 22px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:'8px', color:'white', fontSize:'13px', fontWeight:700, cursor:'pointer', opacity: cpLoading ? 0.6 : 1 }}>
                {cpLoading ? 'Changing…' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Session & Password policy */}
      <div style={{ fontWeight:700, fontSize:'14px', color:'white', marginBottom:'12px' }}>Session & Password Policy</div>
      <div style={{ display:'grid', gap:'14px', marginBottom:'20px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
          <div>
            <label style={{ fontSize:'12px', fontWeight:600, color:'#94a3b8', display:'block', marginBottom:'5px' }}>Session Timeout (minutes)</label>
            <input type="number" value={sessionTimeout} onChange={e => setSessionTimeout(e.target.value)} min="5" max="480"
              style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', padding:'8px 12px', color:'#e2e8f0', fontSize:'13px', outline:'none', boxSizing:'border-box' as const }} />
          </div>
          <div>
            <label style={{ fontSize:'12px', fontWeight:600, color:'#94a3b8', display:'block', marginBottom:'5px' }}>Password Expiry (days, 0=never)</label>
            <input type="number" value={pwExpiry} onChange={e => setPwExpiry(e.target.value)} min="0"
              style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', padding:'8px 12px', color:'#e2e8f0', fontSize:'13px', outline:'none', boxSizing:'border-box' as const }} />
          </div>
        </div>
        <div>
          <label style={{ fontSize:'12px', fontWeight:600, color:'#94a3b8', display:'block', marginBottom:'5px' }}>Minimum Password Length</label>
          <input type="number" value={pwMinLen} onChange={e => setPwMinLen(e.target.value)} min="8" max="64"
            style={{ width:'160px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', padding:'8px 12px', color:'#e2e8f0', fontSize:'13px', outline:'none' }} />
        </div>
        <div>
          <label style={{ fontSize:'12px', fontWeight:600, color:'#94a3b8', display:'block', marginBottom:'5px' }}>IP Allowlist (one per line, leave blank for any)</label>
          <textarea value={ipWhitelist} onChange={e => setIpWhitelist(e.target.value)} rows={3} placeholder={'192.168.1.0/24\n10.0.0.0/8'}
            style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', padding:'8px 12px', color:'#e2e8f0', fontSize:'12px', outline:'none', resize:'vertical', fontFamily:'monospace', boxSizing:'border-box' as const }} />
        </div>
      </div>
      <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
        <button onClick={() => { setSaved(true); setTimeout(() => setSaved(false), 2000); toast('Security policy saved', 'success'); }} style={{ padding:'9px 22px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:'8px', color:'white', fontSize:'13px', fontWeight:700, cursor:'pointer' }}>
          Save Policy
        </button>
        {saved && <span style={{ fontSize:'12px', color:'#22c55e', fontWeight:600 }}>✓ Saved</span>}
      </div>
    </div>
  );
}

// ─── Notifications Tab ────────────────────────────────────────────────────────

function NotificationsTab() {
  const { toast } = useToast();
  const [emailEnabled, setEmailEnabled] = React.useState(true);
  const [smsEnabled, setSmsEnabled]     = React.useState(false);
  const [pushEnabled, setPushEnabled]   = React.useState(false);
  const [smtpHost, setSmtpHost]         = React.useState('smtp.office365.com');
  const [smtpPort, setSmtpPort]         = React.useState('587');
  const [smtpUser, setSmtpUser]         = React.useState('alerts@council.nsw.gov.au');
  const [smtpPass, setSmtpPass]         = React.useState('');
  const [smsProvider, setSmsProvider]   = React.useState('twilio');
  const [smsApiKey, setSmsApiKey]       = React.useState('');
  const [smsFrom, setSmsFrom]           = React.useState('+61412345678');
  const [saved, setSaved] = React.useState(false);
  const [testing, setTesting] = React.useState('');

  type NotifRule = { id:string; event:string; channels:string[]; roles:string[]; enabled:boolean };
  const [rules, setRules] = React.useState<NotifRule[]>([
    { id:'r1', event:'Critical Alarm — no ACK in 5min', channels:['email','sms'],  roles:['supervisor','admin'], enabled:true },
    { id:'r2', event:'New Alarm (Critical/High)',        channels:['email'],        roles:['operator','supervisor'], enabled:true },
    { id:'r3', event:'Lone Worker Overdue',              channels:['email','sms'],  roles:['supervisor'],           enabled:true },
    { id:'r4', event:'Integration Offline',             channels:['email'],        roles:['admin'],                enabled:true },
    { id:'r5', event:'Daily Shift Handover Report',     channels:['email'],        roles:['supervisor','admin'],   enabled:false },
    { id:'r6', event:'User Login from Unknown IP',      channels:['email','push'], roles:['admin'],                enabled:true },
  ]);

  function toggleRuleChannel(id:string, channel:string) {
    setRules(prev => prev.map(r => r.id!==id ? r : {
      ...r, channels: r.channels.includes(channel) ? r.channels.filter(c=>c!==channel) : [...r.channels,channel]
    }));
  }
  function toggleRule(id:string) { setRules(prev => prev.map(r => r.id===id ? {...r,enabled:!r.enabled} : r)); }

  async function handleTest(channel: string) {
    setTesting(channel + '_loading');
    const tok = localStorage.getItem('lemai_token') || '';
    try {
      if (channel === 'email') {
        const r = await fetch('/api/notify/test-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
          body: JSON.stringify({ host: smtpHost, port: parseInt(smtpPort), username: smtpUser, password: smtpPass }),
        });
        const d = await r.json();
        setTesting(d.ok ? 'email_ok' : 'email_err');
        toast(d.ok ? 'Test email sent successfully' : `Email test failed: ${d.error || 'Unknown error'}`, d.ok ? 'success' : 'error');
      } else if (channel === 'sms') {
        const r = await fetch('/api/notify/test-sms', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
          body: JSON.stringify({ provider: smsProvider, api_key: smsApiKey, from: smsFrom }),
        });
        const d = await r.json();
        setTesting(d.ok ? 'sms_ok' : 'sms_err');
        toast(d.ok ? 'Test SMS sent successfully' : `SMS test failed: ${d.error || 'Unknown error'}`, d.ok ? 'success' : 'error');
      }
    } catch {
      setTesting(channel + '_err');
      toast(`${channel.toUpperCase()} test failed — check backend connection`, 'error');
    }
    setTimeout(() => setTesting(''), 3000);
  }

  const Switch = ({ on, onToggle }: { on:boolean; onToggle:()=>void }) => (
    <div onClick={onToggle} style={{ width:'38px', height:'22px', borderRadius:'11px', background:on?'#22c55e':'rgba(255,255,255,0.1)', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
      <div style={{ position:'absolute', top:'3px', left:on?'19px':'3px', width:'16px', height:'16px', borderRadius:'50%', background:'white', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }} />
    </div>
  );

  return (
    <div style={{ padding:'20px', maxWidth:'700px' }}>
      {/* Channels */}
      <div style={{ fontWeight:700, fontSize:'14px', color:'white', marginBottom:'12px' }}>Delivery Channels</div>
      <div style={{ display:'grid', gap:'12px', marginBottom:'24px' }}>

        {/* Email */}
        <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', padding:'16px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: emailEnabled?'14px':'0' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <span style={{ fontSize:'20px' }}>📧</span>
              <div>
                <div style={{ fontSize:'13px', fontWeight:600, color:'white' }}>Email (SMTP)</div>
                <div style={{ fontSize:'11px', color:'#64748b' }}>Send alarm and system notifications via email</div>
              </div>
            </div>
            <Switch on={emailEnabled} onToggle={() => setEmailEnabled(v=>!v)} />
          </div>
          {emailEnabled && !smtpPass && (
            <div style={{ background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:'8px', padding:'10px 14px', marginBottom:'14px', display:'flex', alignItems:'center', gap:'10px' }}>
              <span style={{ fontSize:'14px', flexShrink:0 }}>🔴</span>
              <div style={{ fontSize:'11px', color:'#fca5a5', lineHeight:1.5 }}>
                <strong>SMTP not configured.</strong> Critical alarm emails will not be delivered until an SMTP password is saved and tested.
              </div>
            </div>
          )}
          {emailEnabled && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 100px', gap:'10px' }}>
              <div>
                <label style={{ fontSize:'11px', color:'#64748b', display:'block', marginBottom:'4px' }}>SMTP Host</label>
                <input value={smtpHost} onChange={e=>setSmtpHost(e.target.value)} style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'7px', padding:'7px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none', boxSizing:'border-box' as const }} />
              </div>
              <div>
                <label style={{ fontSize:'11px', color:'#64748b', display:'block', marginBottom:'4px' }}>Port</label>
                <input value={smtpPort} onChange={e=>setSmtpPort(e.target.value)} style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'7px', padding:'7px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none', boxSizing:'border-box' as const }} />
              </div>
              <div>
                <label style={{ fontSize:'11px', color:'#64748b', display:'block', marginBottom:'4px' }}>Username / From Address</label>
                <input value={smtpUser} onChange={e=>setSmtpUser(e.target.value)} style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'7px', padding:'7px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none', boxSizing:'border-box' as const }} />
              </div>
              <div>
                <label style={{ fontSize:'11px', color:'#64748b', display:'block', marginBottom:'4px' }}>Password</label>
                <input type="password" value={smtpPass} onChange={e=>setSmtpPass(e.target.value)} placeholder="••••••••" style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'7px', padding:'7px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none', boxSizing:'border-box' as const }} />
              </div>
              <div style={{ gridColumn:'1/-1', display:'flex', justifyContent:'flex-end' }}>
                <button onClick={() => handleTest('email')} disabled={testing==='email_loading'} style={{ padding:'6px 14px', background: testing==='email_ok' ? 'rgba(34,197,94,0.12)' : testing==='email_err' ? 'rgba(239,68,68,0.12)' : 'rgba(59,130,246,0.12)', border:`1px solid ${testing==='email_ok'?'rgba(34,197,94,0.3)':testing==='email_err'?'rgba(239,68,68,0.3)':'rgba(59,130,246,0.3)'}`, borderRadius:'7px', color: testing==='email_ok' ? '#22c55e' : testing==='email_err' ? '#f87171' : '#3b82f6', fontSize:'11px', fontWeight:600, cursor: testing==='email_loading' ? 'wait' : 'pointer' }}>
                  {testing==='email_loading' ? '⏳ Sending…' : testing==='email_ok' ? '✓ Test email sent' : testing==='email_err' ? '✕ Send failed' : '⚡ Send Test Email'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* SMS */}
        <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', padding:'16px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: smsEnabled?'14px':'0' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <span style={{ fontSize:'20px' }}>📱</span>
              <div>
                <div style={{ fontSize:'13px', fontWeight:600, color:'white' }}>SMS</div>
                <div style={{ fontSize:'11px', color:'#64748b' }}>Twilio, MessageBird or AWS SNS</div>
              </div>
            </div>
            <Switch on={smsEnabled} onToggle={() => setSmsEnabled(v=>!v)} />
          </div>
          {smsEnabled && (
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
              <div>
                <label style={{ fontSize:'11px', color:'#64748b', display:'block', marginBottom:'4px' }}>Provider</label>
                <select value={smsProvider} onChange={e=>setSmsProvider(e.target.value)} style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'7px', padding:'7px 10px', color:'#e2e8f0', fontSize:'12px', cursor:'pointer' }}>
                  <option value="twilio">Twilio</option>
                  <option value="messagebird">MessageBird</option>
                  <option value="sns">AWS SNS</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize:'11px', color:'#64748b', display:'block', marginBottom:'4px' }}>From Number</label>
                <input value={smsFrom} onChange={e=>setSmsFrom(e.target.value)} style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'7px', padding:'7px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none', boxSizing:'border-box' as const }} />
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={{ fontSize:'11px', color:'#64748b', display:'block', marginBottom:'4px' }}>API Key / Auth Token</label>
                <input type="password" value={smsApiKey} onChange={e=>setSmsApiKey(e.target.value)} placeholder="••••••••" style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'7px', padding:'7px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none', boxSizing:'border-box' as const }} />
              </div>
              <div style={{ gridColumn:'1/-1', display:'flex', justifyContent:'flex-end' }}>
                <button onClick={() => handleTest('sms')} disabled={testing==='sms_loading'} style={{ padding:'6px 14px', background: testing==='sms_ok' ? 'rgba(34,197,94,0.12)' : testing==='sms_err' ? 'rgba(239,68,68,0.12)' : 'rgba(59,130,246,0.12)', border:`1px solid ${testing==='sms_ok'?'rgba(34,197,94,0.3)':testing==='sms_err'?'rgba(239,68,68,0.3)':'rgba(59,130,246,0.3)'}`, borderRadius:'7px', color: testing==='sms_ok' ? '#22c55e' : testing==='sms_err' ? '#f87171' : '#3b82f6', fontSize:'11px', fontWeight:600, cursor: testing==='sms_loading' ? 'wait' : 'pointer' }}>
                  {testing==='sms_loading' ? '⏳ Sending…' : testing==='sms_ok' ? '✓ Test SMS sent' : testing==='sms_err' ? '✕ Send failed' : '⚡ Send Test SMS'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Push */}
        <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', padding:'16px' }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <span style={{ fontSize:'20px' }}>🔔</span>
              <div>
                <div style={{ fontSize:'13px', fontWeight:600, color:'white' }}>Browser Push Notifications</div>
                <div style={{ fontSize:'11px', color:'#64748b' }}>Real-time push when browser is open</div>
              </div>
            </div>
            <Switch on={pushEnabled} onToggle={() => setPushEnabled(v=>!v)} />
          </div>
        </div>
      </div>

      {/* Notification Rules */}
      <div style={{ fontWeight:700, fontSize:'14px', color:'white', marginBottom:'12px' }}>Notification Rules</div>
      <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', overflow:'hidden', marginBottom:'20px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 120px 140px 60px', padding:'8px 14px', borderBottom:'1px solid rgba(255,255,255,0.06)', fontSize:'10px', fontWeight:700, color:'#334155', textTransform:'uppercase', letterSpacing:'0.5px' }}>
          <span>Event</span><span>Channels</span><span>Roles</span><span>On</span>
        </div>
        {rules.map(r => (
          <div key={r.id} style={{ display:'grid', gridTemplateColumns:'1fr 120px 140px 60px', padding:'10px 14px', borderBottom:'1px solid rgba(255,255,255,0.04)', alignItems:'center', opacity: r.enabled ? 1 : 0.5 }}>
            <span style={{ fontSize:'12px', color:'#e2e8f0' }}>{r.event}</span>
            <div style={{ display:'flex', gap:'4px' }}>
              {(['email','sms','push'] as const).filter(c => emailEnabled||smsEnabled||pushEnabled||true).map(channel => (
                <button key={channel} onClick={() => toggleRuleChannel(r.id, channel)}
                  style={{ fontSize:'9px', padding:'2px 6px', borderRadius:'5px', border:`1px solid ${r.channels.includes(channel)?'rgba(59,130,246,0.4)':'rgba(255,255,255,0.08)'}`, background:r.channels.includes(channel)?'rgba(59,130,246,0.12)':'transparent', color:r.channels.includes(channel)?'#93c5fd':'#475569', cursor:'pointer', fontWeight:600 }}>
                  {channel}
                </button>
              ))}
            </div>
            <span style={{ fontSize:'10px', color:'#64748b' }}>{r.roles.join(', ')}</span>
            <Switch on={r.enabled} onToggle={() => toggleRule(r.id)} />
          </div>
        ))}
      </div>

      <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
        <button onClick={() => { setSaved(true); setTimeout(() => setSaved(false), 2000); toast('Notification settings saved', 'success'); }} style={{ padding:'9px 22px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:'8px', color:'white', fontSize:'13px', fontWeight:700, cursor:'pointer' }}>
          Save Notification Settings
        </button>
        {saved && <span style={{ fontSize:'12px', color:'#22c55e', fontWeight:600 }}>✓ Saved</span>}
      </div>
    </div>
  );
}

// ─── On-Call Schedule Tab ────────────────────────────────────────────────────

interface OnCallEntry {
  id: string; name: string; role: string; phone: string; email: string;
  day: string; shiftStart: string; shiftEnd: string; escalationLevel: number;
}

const DAYS_OF_WEEK = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
const ONCALL_ROLES = ['SOC Operator','SOC Supervisor','Night Supervisor','Security Manager','Technical Lead','On-Site Guard'];

function OnCallTab() {
  const { toast } = useToast();
  const tok = localStorage.getItem('lemai_token') || '';
  const [schedule, setSchedule] = React.useState<OnCallEntry[]>([]);
  const [loading, setLoading]   = React.useState(true);
  const [currentOnCall, setCurrentOnCall] = React.useState<OnCallEntry[]>([]);
  const [filterDay, setFilterDay] = React.useState('All');
  const [showAdd, setShowAdd]   = React.useState(false);
  const [deleting, setDeleting] = React.useState<string|null>(null);

  const [newEntry, setNewEntry] = React.useState<Partial<OnCallEntry>>({
    name:'', role:'SOC Operator', phone:'', email:'', day:'Monday',
    shiftStart:'06:00', shiftEnd:'14:00', escalationLevel:1,
  });

  function load() {
    setLoading(true);
    fetch('/api/oncall', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : { schedule: [] })
      .then(d => setSchedule(d.schedule || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    fetch('/api/oncall/current', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : { onCall: [] })
      .then(d => setCurrentOnCall(d.onCall || []))
      .catch(() => {});
  }

  React.useEffect(() => { load(); }, []);

  async function addEntry() {
    if (!newEntry.name?.trim()) { toast('Name is required', 'error'); return; }
    const r = await fetch('/api/oncall', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
      body: JSON.stringify(newEntry),
    });
    if (r.ok) { toast('Entry added', 'success'); load(); setShowAdd(false); }
    else toast('Failed to add entry', 'error');
  }

  async function deleteEntry(id: string) {
    setDeleting(id);
    const r = await fetch(`/api/oncall/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${tok}` } });
    if (r.ok) { toast('Entry removed', 'success'); load(); }
    else toast('Failed to remove entry', 'error');
    setDeleting(null);
  }

  const LEVEL_COLOR: Record<number, string> = { 1:'#3b82f6', 2:'#f59e0b', 3:'#ef4444' };
  const LEVEL_LABEL: Record<number, string> = { 1:'L1 Operator', 2:'L2 Supervisor', 3:'L3 Management' };

  const filtered = filterDay === 'All' ? schedule : schedule.filter(e => e.day === filterDay);

  return (
    <div style={{ padding:'20px' }}>
      {/* Current on-call banner */}
      {currentOnCall.length > 0 && (
        <div style={{ background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'12px', padding:'16px', marginBottom:'20px' }}>
          <div style={{ fontSize:'12px', fontWeight:700, color:'#22c55e', marginBottom:'10px' }}>🟢 Currently On-Call</div>
          <div style={{ display:'flex', gap:'12px', flexWrap:'wrap' }}>
            {currentOnCall.map(e => (
              <div key={e.id} style={{ background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'8px', padding:'10px 14px', minWidth:'180px' }}>
                <div style={{ fontSize:'13px', fontWeight:700, color:'#e2e8f0' }}>{e.name}</div>
                <div style={{ fontSize:'11px', color:'#64748b', marginBottom:'4px' }}>{e.role}</div>
                <div style={{ fontSize:'11px', color:'#22c55e', fontFamily:'monospace' }}>{e.phone}</div>
                <div style={{ fontSize:'10px', color:'#475569', marginTop:'2px' }}>L{e.escalationLevel} · {e.shiftStart}–{e.shiftEnd}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter + Add */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
        <div style={{ display:'flex', gap:'6px', alignItems:'center' }}>
          <span style={{ fontSize:'12px', color:'#64748b' }}>Day:</span>
          <select value={filterDay} onChange={e => setFilterDay(e.target.value)}
            style={{ background:'#1e293b', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'5px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none' }}>
            <option>All</option>
            {DAYS_OF_WEEK.map(d => <option key={d}>{d}</option>)}
          </select>
        </div>
        <button onClick={() => setShowAdd(v => !v)}
          style={{ padding:'6px 16px', borderRadius:'8px', border:'none', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>
          {showAdd ? 'Cancel' : '+ Add Entry'}
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div style={{ background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.2)', borderRadius:'12px', padding:'16px', marginBottom:'16px' }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px', marginBottom:'10px' }}>
            {[['Name *','name','text'],['Phone','phone','text'],['Email','email','email']].map(([lbl,key,type]) => (
              <div key={key}>
                <label style={{ display:'block', fontSize:'10px', color:'#64748b', marginBottom:'4px' }}>{lbl}</label>
                <input type={type} value={(newEntry as any)[key] || ''} onChange={e => setNewEntry(p => ({...p,[key]:e.target.value}))}
                  style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'6px 8px', color:'#e2e8f0', fontSize:'11px', outline:'none', boxSizing:'border-box' as const }} />
              </div>
            ))}
            <div>
              <label style={{ display:'block', fontSize:'10px', color:'#64748b', marginBottom:'4px' }}>Role</label>
              <select value={newEntry.role || 'SOC Operator'} onChange={e => setNewEntry(p => ({...p,role:e.target.value}))}
                style={{ width:'100%', background:'#1e293b', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'6px 8px', color:'#e2e8f0', fontSize:'11px', outline:'none' }}>
                {ONCALL_ROLES.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:'10px', color:'#64748b', marginBottom:'4px' }}>Day</label>
              <select value={newEntry.day || 'Monday'} onChange={e => setNewEntry(p => ({...p,day:e.target.value}))}
                style={{ width:'100%', background:'#1e293b', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'6px 8px', color:'#e2e8f0', fontSize:'11px', outline:'none' }}>
                {DAYS_OF_WEEK.map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:'10px', color:'#64748b', marginBottom:'4px' }}>Escalation Level</label>
              <select value={newEntry.escalationLevel || 1} onChange={e => setNewEntry(p => ({...p,escalationLevel:Number(e.target.value)}))}
                style={{ width:'100%', background:'#1e293b', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'6px 8px', color:'#e2e8f0', fontSize:'11px', outline:'none' }}>
                <option value={1}>L1 — First Responder</option>
                <option value={2}>L2 — Supervisor</option>
                <option value={3}>L3 — Management</option>
              </select>
            </div>
            <div>
              <label style={{ display:'block', fontSize:'10px', color:'#64748b', marginBottom:'4px' }}>Shift Start</label>
              <input type="time" value={newEntry.shiftStart || '06:00'} onChange={e => setNewEntry(p => ({...p,shiftStart:e.target.value}))}
                style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'6px 8px', color:'#e2e8f0', fontSize:'11px', outline:'none' }} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:'10px', color:'#64748b', marginBottom:'4px' }}>Shift End</label>
              <input type="time" value={newEntry.shiftEnd || '14:00'} onChange={e => setNewEntry(p => ({...p,shiftEnd:e.target.value}))}
                style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'6px 8px', color:'#e2e8f0', fontSize:'11px', outline:'none' }} />
            </div>
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px' }}>
            <button onClick={() => setShowAdd(false)} style={{ padding:'7px 16px', background:'transparent', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', color:'#64748b', fontSize:'11px', cursor:'pointer' }}>Cancel</button>
            <button onClick={addEntry} style={{ padding:'7px 20px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:'7px', color:'white', fontSize:'11px', fontWeight:700, cursor:'pointer' }}>Save Entry</button>
          </div>
        </div>
      )}

      {/* Schedule table */}
      {loading ? (
        <div style={{ textAlign:'center', padding:'30px', color:'#475569', fontSize:'12px' }}>Loading schedule…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'30px', color:'#334155', fontSize:'12px' }}>No on-call entries for this day.</div>
      ) : (
        <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 100px 120px 80px 40px', padding:'8px 16px', borderBottom:'1px solid rgba(255,255,255,0.06)', fontSize:'10px', fontWeight:700, color:'#334155', textTransform:'uppercase', letterSpacing:'0.5px' }}>
            <span>Name / Role</span><span>Contact</span><span>Day</span><span>Shift</span><span>Level</span><span></span>
          </div>
          {filtered.map(e => (
            <div key={e.id} style={{ display:'grid', gridTemplateColumns:'1fr 1fr 100px 120px 80px 40px', padding:'10px 16px', borderBottom:'1px solid rgba(255,255,255,0.04)', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:'12px', fontWeight:600, color:'#e2e8f0' }}>{e.name}</div>
                <div style={{ fontSize:'10px', color:'#64748b' }}>{e.role}</div>
              </div>
              <div>
                <div style={{ fontSize:'11px', color:'#94a3b8', fontFamily:'monospace' }}>{e.phone}</div>
                <div style={{ fontSize:'10px', color:'#475569' }}>{e.email}</div>
              </div>
              <div style={{ fontSize:'11px', color:'#64748b' }}>{e.day}</div>
              <div style={{ fontSize:'11px', color:'#94a3b8', fontFamily:'monospace' }}>{e.shiftStart}–{e.shiftEnd}</div>
              <div>
                <span style={{ fontSize:'9px', fontWeight:700, padding:'2px 6px', borderRadius:'6px', background:`${LEVEL_COLOR[e.escalationLevel] || '#64748b'}15`, color:LEVEL_COLOR[e.escalationLevel] || '#64748b' }}>
                  {LEVEL_LABEL[e.escalationLevel] || `L${e.escalationLevel}`}
                </span>
              </div>
              <div>
                <button onClick={() => deleteEntry(e.id)} disabled={deleting === e.id}
                  style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:'5px', color:'#ef4444', fontSize:'10px', cursor:'pointer', padding:'2px 6px' }}>
                  {deleting === e.id ? '…' : '✕'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Suppression Rules Panel ─────────────────────────────────────────────────

function SuppressionPanel() {
  const { toast } = useToast();
  const tok = localStorage.getItem('lemai_token') || '';
  const [rules, setRules] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [site, setSite] = React.useState('');
  const [location, setLocation] = React.useState('');
  const [alarmType, setAlarmType] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [durationMinutes, setDurationMinutes] = React.useState(60);
  const [adding, setAdding] = React.useState(false);

  function load() {
    setLoading(true);
    fetch('/api/armorip/suppression', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : { rules: [] })
      .then(d => setRules(d.rules || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }

  React.useEffect(() => { load(); }, []);

  async function addRule() {
    if (!reason.trim()) { toast('Reason is required', 'error'); return; }
    setAdding(true);
    try {
      const r = await fetch('/api/armorip/suppression', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ site, location, alarmType, reason, durationMinutes }),
      });
      if (r.ok) { toast('Suppression rule added', 'success'); load(); setSite(''); setLocation(''); setAlarmType(''); setReason(''); }
      else toast('Failed to add rule', 'error');
    } catch { toast('Network error', 'error'); }
    setAdding(false);
  }

  async function removeRule(id: string) {
    const r = await fetch(`/api/armorip/suppression/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${tok}` } });
    if (r.ok) { toast('Rule removed', 'success'); load(); }
  }

  const active = rules.filter(r => r.active);
  const expired = rules.filter(r => !r.active);

  return (
    <div style={{ marginTop:'24px', background:'rgba(239,68,68,0.04)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'12px', padding:'20px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'16px' }}>
        <span style={{ fontSize:'18px' }}>🔕</span>
        <div>
          <div style={{ fontSize:'14px', fontWeight:700, color:'#e2e8f0' }}>Alarm Suppression</div>
          <div style={{ fontSize:'11px', color:'#64748b' }}>Suppress alarms from specific zones during maintenance windows. Suppressed alarms are still logged.</div>
        </div>
      </div>

      {/* Add rule form */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', marginBottom:'12px' }}>
        <div>
          <label style={{ display:'block', fontSize:'10px', color:'#64748b', marginBottom:'4px' }}>Site (leave blank = all)</label>
          <input value={site} onChange={e => setSite(e.target.value)} placeholder="e.g. Town Hall"
            style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'6px 10px', color:'#e2e8f0', fontSize:'11px', outline:'none', boxSizing:'border-box' as const }} />
        </div>
        <div>
          <label style={{ display:'block', fontSize:'10px', color:'#64748b', marginBottom:'4px' }}>Location (blank = any)</label>
          <input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Server Room"
            style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'6px 10px', color:'#e2e8f0', fontSize:'11px', outline:'none', boxSizing:'border-box' as const }} />
        </div>
        <div>
          <label style={{ display:'block', fontSize:'10px', color:'#64748b', marginBottom:'4px' }}>Alarm Type (blank = any)</label>
          <input value={alarmType} onChange={e => setAlarmType(e.target.value)} placeholder="e.g. Motion Detected"
            style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'6px 10px', color:'#e2e8f0', fontSize:'11px', outline:'none', boxSizing:'border-box' as const }} />
        </div>
        <div>
          <label style={{ display:'block', fontSize:'10px', color:'#64748b', marginBottom:'4px' }}>Reason *</label>
          <input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. HVAC maintenance"
            style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'6px 10px', color:'#e2e8f0', fontSize:'11px', outline:'none', boxSizing:'border-box' as const }} />
        </div>
        <div>
          <label style={{ display:'block', fontSize:'10px', color:'#64748b', marginBottom:'4px' }}>Duration (minutes)</label>
          <input type="number" value={durationMinutes} onChange={e => setDurationMinutes(Number(e.target.value))} min={1} max={1440}
            style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'6px 10px', color:'#e2e8f0', fontSize:'11px', outline:'none', boxSizing:'border-box' as const }} />
        </div>
        <div style={{ display:'flex', alignItems:'flex-end' }}>
          <button onClick={addRule} disabled={adding}
            style={{ width:'100%', padding:'7px', borderRadius:'7px', border:'none', background:'rgba(239,68,68,0.2)', color:'#f87171', fontSize:'11px', fontWeight:700, cursor:'pointer' }}>
            {adding ? 'Adding…' : '+ Add Suppression'}
          </button>
        </div>
      </div>

      {/* Active rules */}
      {loading && <div style={{ fontSize:'11px', color:'#475569', textAlign:'center', padding:'8px' }}>Loading…</div>}
      {!loading && active.length === 0 && <div style={{ fontSize:'11px', color:'#334155', padding:'8px' }}>No active suppression rules.</div>}
      {active.map(r => (
        <div key={r.id} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'8px 10px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'8px', marginBottom:'4px' }}>
          <span style={{ fontSize:'12px' }}>🔕</span>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={{ fontSize:'11px', color:'#f87171', fontWeight:600 }}>
              {[r.site||'All Sites', r.location||'All Locations', r.alarmType||'All Types'].join(' › ')}
            </div>
            <div style={{ fontSize:'10px', color:'#64748b' }}>{r.reason} · Expires: {new Date(r.expiresAt * 1000).toLocaleTimeString('en-AU', { hour:'2-digit', minute:'2-digit' })}</div>
          </div>
          <button onClick={() => removeRule(r.id)} style={{ padding:'3px 8px', background:'rgba(239,68,68,0.15)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:'5px', color:'#ef4444', fontSize:'10px', fontWeight:700, cursor:'pointer' }}>Remove</button>
        </div>
      ))}
      {expired.length > 0 && (
        <div style={{ fontSize:'10px', color:'#334155', marginTop:'8px' }}>{expired.length} expired rule{expired.length > 1 ? 's' : ''} (not shown)</div>
      )}
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

type Tab = 'cameras' | 'integrations' | 'controllers' | 'system' | 'camera-linking' | 'sop-manager' | 'alarm-types' | 'escalation' | 'alarm-notifications' | 'security' | 'notifications' | 'armorip' | 'sessions' | 'oncall' | 'branding' | 'email';

interface AlarmTypeConfig {
  displayName: string;
  category: string;
  sop: string[];
  aliases: string[];   // raw Patriot/incoming strings that map to this canonical type
}

const ALARM_CATEGORIES = ['Security', 'Technical', 'Fire', 'Medical', 'Access', 'Environmental', 'Other'];

// ─── Seed data for new tabs ───────────────────────────────────────────────────

const SOP_ALARM_TYPES = [
  'Duress Button',
  'Door Forced Open',
  'Fire Panel Input',
  'Perimeter Breach',
  'Camera Tamper',
  'Motion Detected',
  'Glass Break',
  'Power Failure',
  'Loitering',
];

const DEFAULT_SOPS: Record<string, string[]> = {
  'Duress Button':    ['Immediately call affected area on intercom','Dispatch nearest guard','Call 000 if no response within 60 seconds','Notify supervisor and escalate to critical','Log all actions in job note','Do not close job until cleared by supervisor'],
  'Door Forced Open': ['Identify door location on floor plan','View linked camera feed','Dispatch guard to location','Check for tailgating or forced entry','If breach confirmed, call 000','Lock down adjacent doors via Access Control','Update job with findings'],
  'Fire Panel Input': ['Check fire panel zone on SOC dashboard','Dispatch guard to confirm visual','Call 000 — do not wait for second confirmation','Initiate evacuation if confirmed','Notify site manager immediately','Isolate zone in panel if false alarm confirmed'],
  'Perimeter Breach': ['Review camera feed for confirmed intrusion','Track movement across floor plan','Dispatch guard with radio contact','Notify supervisor','Call 000 if intruder located','Document all observations in job'],
  'Camera Tamper':    ['Identify tampered camera location','Check adjacent cameras for context','Dispatch guard to inspect physical camera','Check for signs of vandalism or deliberate concealment','Log fault and escalate to technical team if hardware damaged'],
  'Motion Detected':  ['Review camera feed — confirm true vs false positive','Check time of day and area context','Dispatch guard if motion confirmed after hours','Log outcome in job note'],
  'Glass Break':      ['Identify zone from alarm panel','Review camera feed for broken glass or forced entry','Dispatch guard immediately','Call 000 if entry confirmed','Notify site manager','Secure adjacent areas'],
  'Power Failure':    ['Confirm UPS status on dashboard','Check generator status','Notify technical team immediately','Confirm cameras and access control on backup power','Log all affected systems in job'],
  'Loitering':        ['Review camera feed — assess threat level','Dispatch guard for welfare check','If threatening behaviour, call 000','Log outcome and individual description in job'],
};

interface EscalationRule {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  alarmType: string;
  ackTimeout: number; // minutes
  escalateTo: string;
  action: string;
  enabled: boolean;
}

const SEED_ESCALATION: EscalationRule[] = [
  { id:'ESC-001', severity:'critical', alarmType:'Duress Button',    ackTimeout:2,  escalateTo:'Supervisor + 000',            action:'Auto-call supervisor, page 000',          enabled:true },
  { id:'ESC-002', severity:'critical', alarmType:'Fire Panel Input', ackTimeout:1,  escalateTo:'Site Manager + 000',          action:'Auto-call site manager, alert fire brigade',enabled:true },
  { id:'ESC-003', severity:'high',     alarmType:'Door Forced Open', ackTimeout:5,  escalateTo:'Supervisor',                  action:'Page supervisor, lock adjacent doors',    enabled:true },
  { id:'ESC-004', severity:'high',     alarmType:'Glass Break',      ackTimeout:5,  escalateTo:'Supervisor',                  action:'Page supervisor, call police if confirmed',enabled:true },
  { id:'ESC-005', severity:'medium',   alarmType:'Perimeter Breach', ackTimeout:10, escalateTo:'Senior Operator',             action:'Escalate to senior SOC operator',         enabled:true },
  { id:'ESC-006', severity:'medium',   alarmType:'Camera Tamper',    ackTimeout:15, escalateTo:'Technical Team',              action:'Notify tech team, create fault job',      enabled:true },
  { id:'ESC-007', severity:'low',      alarmType:'Motion Detected',  ackTimeout:30, escalateTo:'Operator (reminder)',         action:'Send reminder notification to operator',  enabled:false },
  { id:'ESC-008', severity:'low',      alarmType:'Loitering',        ackTimeout:20, escalateTo:'Operator (reminder)',         action:'Send reminder notification to operator',  enabled:true },
];

// ─── Seed controllers (Protege GX fallback for demo mode) ──────────────────
interface CtlDevice {
  id: string; name: string; site: string; ip: string; doors: number;
  readers: number; status: 'online'|'offline'; firmware: string; lastSeen: string; health: string;
}
interface CtlDoor {
  id: string|number; name: string; controller: string; controllerId: string;
  status: string; lastEvent?: string;
}
const SEED_CONTROLLERS: CtlDevice[] = [
  { id:'CTL-01', name:'Town Hall Main Controller',    site:'Town Hall',        ip:'10.10.1.11', doors:3, readers:6, status:'online',  firmware:'v3.4.0', lastSeen:'Just now',   health:'OK' },
  { id:'CTL-02', name:'Civic Centre Controller',      site:'Civic Centre',     ip:'10.10.2.11', doors:3, readers:5, status:'online',  firmware:'v3.4.0', lastSeen:'Just now',   health:'OK' },
  { id:'CTL-03', name:'Aquatic Centre Controller',    site:'Aquatic Centre',   ip:'10.10.3.11', doors:2, readers:3, status:'online',  firmware:'v3.3.2', lastSeen:'2 min ago',  health:'Firmware outdated' },
  { id:'CTL-04', name:'Council Depot Controller',     site:'Council Depot',    ip:'10.10.4.11', doors:2, readers:4, status:'online',  firmware:'v3.4.0', lastSeen:'Just now',   health:'OK' },
  { id:'CTL-05', name:'Parramatta Square Controller', site:'Parramatta Square',ip:'10.10.5.11', doors:1, readers:2, status:'offline', firmware:'v3.2.1', lastSeen:'14 min ago', health:'Offline — reconnecting' },
];
const SEED_CTL_DOORS: CtlDoor[] = [
  { id:1,  name:'Main Entry',          controller:'CTL-01', controllerId:'CTL-01', status:'unlocked',   lastEvent:'Card accepted — Wei Zhang' },
  { id:2,  name:'Server Room B2',      controller:'CTL-01', controllerId:'CTL-01', status:'locked',     lastEvent:'Door secured (scheduled)' },
  { id:3,  name:'Stairwell North',     controller:'CTL-01', controllerId:'CTL-01', status:'locked',     lastEvent:'No recent activity' },
  { id:4,  name:'Main Entry',          controller:'CTL-02', controllerId:'CTL-02', status:'unlocked',   lastEvent:'Card accepted — J. Okafor' },
  { id:5,  name:'Loading Dock',        controller:'CTL-02', controllerId:'CTL-02', status:'locked',     lastEvent:'Door secured (scheduled)' },
  { id:6,  name:'Atrium',              controller:'CTL-02', controllerId:'CTL-02', status:'unlocked',   lastEvent:'No recent activity' },
  { id:7,  name:'Pool Entry',          controller:'CTL-03', controllerId:'CTL-03', status:'unlocked',   lastEvent:'Card accepted — S. Patel' },
  { id:8,  name:'Plant Room',          controller:'CTL-03', controllerId:'CTL-03', status:'locked',     lastEvent:'Access denied — unknown card' },
  { id:9,  name:'Entry Gate',          controller:'CTL-04', controllerId:'CTL-04', status:'unlocked',   lastEvent:'Card accepted — R. Torres' },
  { id:10, name:'Vehicle Bay',         controller:'CTL-04', controllerId:'CTL-04', status:'locked',     lastEvent:'No recent activity' },
  { id:11, name:'Main Entry Roof',     controller:'CTL-05', controllerId:'CTL-05', status:'unknown',    lastEvent:'Controller offline' },
];

// ─── Alarm zone type (for Camera Linking) ─────────────────────────────────
interface ArmorZone { site: string; location: string; alarmType: string; count: number; }
interface ZoneCameraLink {
  cameras: string[];  // camera IDs or names from registry
  manualCameras: { name: string; description: string }[];
}

// ─── Email / SMTP Tab ────────────────────────────────────────────────────────
function EmailTab() {
  const { toast } = useToast();
  const [cfg, setCfg] = useState({ host: '', port: 587, username: '', password: '', from_email: 'noreply@lemai.ai', to_emails: '', enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [retainDays, setRetainDays] = useState(90);
  const [purging, setPurging] = useState(false);

  useEffect(() => {
    const tok = localStorage.getItem('lemai_token') || '';
    fetch('/api/notify/smtp-config', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCfg({ ...cfg, ...d, password: '' }); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function save() {
    const tok = localStorage.getItem('lemai_token') || '';
    setSaving(true);
    try {
      const body = { ...cfg };
      if (!body.password) delete (body as any).password; // keep existing if blank
      const res = await fetch('/api/notify/smtp-config', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Save failed');
      toast('SMTP settings saved', 'success');
    } catch {
      toast('Failed to save SMTP settings', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function sendTest() {
    const tok = localStorage.getItem('lemai_token') || '';
    setTesting(true);
    try {
      const res = await fetch('/api/notify/test-email', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ host: cfg.host, port: cfg.port, username: cfg.username, password: cfg.password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Test failed');
      toast(data.message || 'Test email sent!', 'success');
    } catch (err: any) {
      toast(err.message || 'Test email failed', 'error');
    } finally {
      setTesting(false);
    }
  }

  function downloadAuditCSV() {
    const tok = localStorage.getItem('lemai_token') || '';
    const url = `/api/audit/export.csv?limit=10000`;
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', `lemai_audit_${new Date().toISOString().slice(0,10)}.csv`);
    // Need to fetch with auth header then blob
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.blob())
      .then(blob => {
        const objUrl = URL.createObjectURL(blob);
        a.href = objUrl;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(objUrl);
      })
      .catch(() => toast('Export failed', 'error'));
  }

  async function purgeAuditLog() {
    if (!window.confirm(`This will permanently delete audit entries older than ${retainDays} days. This cannot be undone. Continue?`)) return;
    setPurging(true);
    try {
      const tok = localStorage.getItem('lemai_token') || '';
      const res = await fetch('/api/audit/purge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ retain_days: retainDays }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Purge failed');
      toast(`Deleted ${data.deleted} entries. ${data.remaining} entries remain.`, 'success');
    } catch (err: any) {
      toast(err.message || 'Purge failed', 'error');
    } finally {
      setPurging(false);
    }
  }

  const field = { background:'rgba(255,255,255,0.05)' as const, border:'1px solid rgba(255,255,255,0.1)' as const, borderRadius:'8px' as const, padding:'9px 12px' as const, color:'#e2e8f0' as const, fontSize:'13px' as const, outline:'none' as const, boxSizing:'border-box' as const, width:'100%' as const };
  const label = { display:'block' as const, fontSize:'10px' as const, fontWeight:600 as const, color:'#64748b' as const, marginBottom:'5px' as const, textTransform:'uppercase' as const, letterSpacing:'0.5px' as const };

  if (loading) return <div style={{ padding:'24px', color:'#475569', fontSize:'13px' }}>Loading SMTP configuration…</div>;

  return (
    <div style={{ maxWidth:'680px', padding:'4px 0' }}>
      {/* SMTP Settings */}
      <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', padding:'20px', marginBottom:'16px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'18px' }}>
          <span style={{ fontSize:'16px' }}>✉</span>
          <div>
            <div style={{ fontSize:'14px', fontWeight:700, color:'#e2e8f0' }}>SMTP Email Configuration</div>
            <div style={{ fontSize:'11px', color:'#64748b' }}>Used for password reset links, handover notifications, and user invitations.</div>
          </div>
          <div style={{ marginLeft:'auto' }}>
            <label style={{ display:'flex', alignItems:'center', gap:'7px', cursor:'pointer', fontSize:'12px', color:'#64748b' }}>
              <div style={{ position:'relative' as const }}>
                <input type="checkbox" checked={cfg.enabled} onChange={e => setCfg(c => ({...c, enabled: e.target.checked}))} style={{ opacity:0, position:'absolute', width:0, height:0 }} />
                <div style={{ width:'34px', height:'18px', background: cfg.enabled ? '#22c55e' : 'rgba(255,255,255,0.1)', borderRadius:'9px', transition:'background 0.2s', cursor:'pointer' }}>
                  <div style={{ position:'absolute' as const, top:'2px', left: cfg.enabled ? '18px' : '2px', width:'14px', height:'14px', background:'white', borderRadius:'50%', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.3)' }} />
                </div>
              </div>
              {cfg.enabled ? 'Enabled' : 'Disabled'}
            </label>
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
          <div style={{ gridColumn:'1/-1' }}>
            <label style={label}>SMTP Host</label>
            <input style={field} value={cfg.host} onChange={e => setCfg(c => ({...c, host: e.target.value}))} placeholder="smtp.gmail.com or smtp.office365.com" />
          </div>
          <div>
            <label style={label}>Port</label>
            <input style={field} type="number" value={cfg.port} onChange={e => setCfg(c => ({...c, port: Number(e.target.value)}))} placeholder="587" />
          </div>
          <div>
            <label style={label}>From Email</label>
            <input style={field} value={cfg.from_email} onChange={e => setCfg(c => ({...c, from_email: e.target.value}))} placeholder="noreply@yourorg.com" />
          </div>
          <div>
            <label style={label}>Username</label>
            <input style={field} value={cfg.username} onChange={e => setCfg(c => ({...c, username: e.target.value}))} placeholder="username@yourorg.com" />
          </div>
          <div>
            <label style={label}>Password</label>
            <div style={{ position:'relative' }}>
              <input style={{...field, paddingRight:'36px'}} type={showPass ? 'text' : 'password'} value={cfg.password} onChange={e => setCfg(c => ({...c, password: e.target.value}))} placeholder="Leave blank to keep existing" />
              <button type="button" onClick={() => setShowPass(s => !s)} style={{ position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', color:'#475569', cursor:'pointer', fontSize:'13px', padding:0 }}>{showPass ? '🙈' : '👁'}</button>
            </div>
          </div>
          <div style={{ gridColumn:'1/-1' }}>
            <label style={label}>Default Recipients (comma-separated)</label>
            <input style={field} value={cfg.to_emails} onChange={e => setCfg(c => ({...c, to_emails: e.target.value}))} placeholder="security@yourorg.com, supervisor@yourorg.com" />
            <div style={{ fontSize:'10px', color:'#475569', marginTop:'5px' }}>Used for alarm emails, shift handover notifications, and scheduled reports.</div>
          </div>
        </div>

        <div style={{ display:'flex', gap:'8px', marginTop:'18px' }}>
          <button onClick={save} disabled={saving}
            style={{ padding:'9px 20px', borderRadius:'8px', border:'none', background:saving ? 'rgba(34,197,94,0.3)' : 'linear-gradient(135deg,#22c55e,#16a34a)', color:'white', fontSize:'12px', fontWeight:700, cursor:saving?'not-allowed':'pointer' }}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
          <button onClick={sendTest} disabled={testing || !cfg.host}
            style={{ padding:'9px 18px', borderRadius:'8px', border:'1px solid rgba(59,130,246,0.3)', background:'rgba(59,130,246,0.1)', color:'#60a5fa', fontSize:'12px', fontWeight:600, cursor:(testing||!cfg.host)?'not-allowed':'pointer', opacity:!cfg.host?0.5:1 }}>
            {testing ? 'Sending…' : 'Send Test Email'}
          </button>
        </div>
      </div>

      {/* Audit Log Export */}
      <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', padding:'20px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
            <span style={{ fontSize:'16px' }}>📋</span>
            <div>
              <div style={{ fontSize:'14px', fontWeight:700, color:'#e2e8f0' }}>Audit Log Export</div>
              <div style={{ fontSize:'11px', color:'#64748b' }}>Download up to 10,000 most recent audit events as a CSV file.</div>
            </div>
          </div>
          <button onClick={downloadAuditCSV}
            style={{ padding:'9px 18px', borderRadius:'8px', border:'1px solid rgba(99,102,241,0.3)', background:'rgba(99,102,241,0.1)', color:'#a5b4fc', fontSize:'12px', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' as const }}>
            ↓ Download CSV
          </button>
        </div>
      </div>

      {/* Audit Retention Policy */}
      <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', padding:'20px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'14px' }}>
          <span style={{ fontSize:'16px' }}>🗑️</span>
          <div>
            <div style={{ fontSize:'14px', fontWeight:700, color:'#e2e8f0' }}>Audit Retention Policy</div>
            <div style={{ fontSize:'11px', color:'#64748b' }}>Permanently delete audit entries older than the retention window. GDPR / data minimisation compliance.</div>
          </div>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' as const }}>
          <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
            <span style={{ fontSize:'12px', color:'#94a3b8' }}>Keep entries for</span>
            <input
              type="number" min={1} max={3650} value={retainDays}
              onChange={e => setRetainDays(Math.max(1, parseInt(e.target.value) || 90))}
              style={{ width:'72px', padding:'6px 10px', borderRadius:'7px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', color:'#e2e8f0', fontSize:'13px', outline:'none', textAlign:'center' as const }}
            />
            <span style={{ fontSize:'12px', color:'#94a3b8' }}>days</span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'11px', color:'#475569' }}>
            <span>Common:</span>
            {[30, 90, 180, 365].map(d => (
              <button key={d} onClick={() => setRetainDays(d)}
                style={{ padding:'3px 9px', borderRadius:'6px', border:`1px solid ${retainDays===d?'rgba(239,68,68,0.4)':'rgba(255,255,255,0.08)'}`, background:retainDays===d?'rgba(239,68,68,0.12)':'rgba(255,255,255,0.03)', color:retainDays===d?'#fca5a5':'#64748b', fontSize:'10px', fontWeight:600, cursor:'pointer' }}>
                {d}d
              </button>
            ))}
          </div>
          <button onClick={purgeAuditLog} disabled={purging}
            style={{ marginLeft:'auto', padding:'8px 16px', borderRadius:'8px', border:'1px solid rgba(239,68,68,0.3)', background:purging?'rgba(239,68,68,0.05)':'rgba(239,68,68,0.1)', color:purging?'#64748b':'#fca5a5', fontSize:'12px', fontWeight:600, cursor:purging?'not-allowed':'pointer', whiteSpace:'nowrap' as const }}>
            {purging ? 'Purging…' : `Purge Entries > ${retainDays}d`}
          </button>
        </div>
        <div style={{ marginTop:'10px', fontSize:'10px', color:'#334155', padding:'8px 12px', background:'rgba(239,68,68,0.05)', border:'1px solid rgba(239,68,68,0.1)', borderRadius:'7px' }}>
          ⚠ This action is irreversible. Export a CSV backup first if you need to archive records before purging.
        </div>
      </div>

      {/* Outbound Webhooks */}
      <WebhooksPanel />
    </div>
  );
}

// ─── Outbound Webhooks Panel ──────────────────────────────────────────────────
function WebhooksPanel() {
  const { toast } = useToast();
  const [hooks, setHooks] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [showNew, setShowNew] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const [newUrl, setNewUrl] = React.useState('');
  const [newEvents, setNewEvents] = React.useState<string[]>(['alarm_new']);
  const [saving, setSaving] = React.useState(false);

  const tok = () => localStorage.getItem('lemai_token') || '';
  const hdr = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${tok()}` });

  const ALL_EVENTS = ['alarm_new','alarm_ack','alarm_close','alarm_escalation','sla_breach'];

  React.useEffect(() => {
    fetch('/api/notify/webhooks', { headers: hdr() })
      .then(r => r.json()).then(d => setHooks(Array.isArray(d) ? d : []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function createHook() {
    if (!newUrl.trim()) return;
    setSaving(true);
    try {
      const res = await fetch('/api/notify/webhooks', { method:'POST', headers: hdr(), body: JSON.stringify({ name: newName || 'Webhook', url: newUrl.trim(), events: newEvents, enabled: true }) });
      const data = await res.json();
      if (res.ok) { setHooks(h => [...h, data]); setShowNew(false); setNewName(''); setNewUrl(''); setNewEvents(['alarm_new']); toast('Webhook created', 'success'); }
      else toast(data.error || 'Create failed', 'error');
    } catch { toast('Create failed', 'error'); }
    setSaving(false);
  }

  async function toggleHook(id: string, enabled: boolean) {
    setHooks(h => h.map(x => x.id === id ? { ...x, enabled } : x));
    try { await fetch(`/api/notify/webhooks/${id}`, { method:'PUT', headers: hdr(), body: JSON.stringify({ enabled }) }); } catch { /* silent */ }
  }

  async function deleteHook(id: string) {
    if (!window.confirm('Delete this webhook?')) return;
    setHooks(h => h.filter(x => x.id !== id));
    try { await fetch(`/api/notify/webhooks/${id}`, { method:'DELETE', headers: hdr() }); } catch { /* silent */ }
  }

  async function testHook(id: string) {
    toast('Sending test payload…', 'info');
    try {
      const res = await fetch(`/api/notify/webhooks/${id}/test`, { method:'POST', headers: hdr() });
      const data = await res.json();
      if (res.ok) toast(data.message || 'Test sent', 'success'); else toast(data.error || 'Test failed', 'error');
    } catch { toast('Test failed', 'error'); }
  }

  const inp = { background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'8px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none', width:'100%', boxSizing:'border-box' as const };

  return (
    <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', padding:'20px' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          <span style={{ fontSize:'16px' }}>🔗</span>
          <div>
            <div style={{ fontSize:'14px', fontWeight:700, color:'#e2e8f0' }}>Outbound Webhooks</div>
            <div style={{ fontSize:'11px', color:'#64748b' }}>Push alarm events to Teams, Slack, PagerDuty, or any HTTP endpoint.</div>
          </div>
        </div>
        <button onClick={() => setShowNew(true)} style={{ padding:'7px 14px', borderRadius:'8px', border:'none', background:'#3b82f6', color:'white', fontSize:'12px', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' as const }}>+ Add Webhook</button>
      </div>

      {loading && <div style={{ fontSize:'11px', color:'#475569', padding:'8px 0' }}>Loading…</div>}
      {!loading && hooks.length === 0 && !showNew && (
        <div style={{ textAlign:'center', padding:'20px 0', color:'#334155', fontSize:'12px' }}>
          No webhooks configured. Add one to push alarm events to Teams, Slack, or other services.
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
        {hooks.map(h => (
          <div key={h.id} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'10px', padding:'12px 14px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:'12px', fontWeight:600, color:'#e2e8f0', marginBottom:'3px' }}>{h.name}</div>
                <div style={{ fontSize:'10px', color:'#475569', fontFamily:'monospace', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' as const, maxWidth:'340px' }}>{h.url}</div>
                <div style={{ display:'flex', gap:'4px', marginTop:'5px', flexWrap:'wrap' as const }}>
                  {(h.events || []).map((e: string) => (
                    <span key={e} style={{ fontSize:'9px', padding:'1px 6px', borderRadius:'5px', background:'rgba(59,130,246,0.1)', color:'#3b82f6', fontWeight:600 }}>{e}</span>
                  ))}
                </div>
              </div>
              <div style={{ display:'flex', gap:'6px', alignItems:'center', flexShrink:0 }}>
                <button onClick={() => testHook(h.id)} style={{ padding:'4px 9px', borderRadius:'6px', border:'1px solid rgba(245,158,11,0.3)', background:'rgba(245,158,11,0.08)', color:'#f59e0b', fontSize:'10px', cursor:'pointer' }}>Test</button>
                <div onClick={() => toggleHook(h.id, !h.enabled)} style={{ width:'36px', height:'20px', borderRadius:'10px', background:h.enabled?'#22c55e':'rgba(255,255,255,0.1)', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
                  <div style={{ position:'absolute', top:'2px', left:h.enabled?'18px':'2px', width:'16px', height:'16px', borderRadius:'50%', background:'white', transition:'left 0.2s' }} />
                </div>
                <button onClick={() => deleteHook(h.id)} style={{ padding:'4px 8px', borderRadius:'6px', border:'1px solid rgba(239,68,68,0.2)', background:'transparent', color:'#ef4444', fontSize:'10px', cursor:'pointer' }}>✕</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showNew && (
        <div style={{ background:'rgba(59,130,246,0.05)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'10px', padding:'14px', marginTop:'10px' }}>
          <div style={{ fontSize:'12px', fontWeight:600, color:'#93c5fd', marginBottom:'12px' }}>New Webhook</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
            <div>
              <label style={{ display:'block', fontSize:'10px', color:'#64748b', marginBottom:'4px', textTransform:'uppercase', fontWeight:600 }}>Name</label>
              <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="e.g. MS Teams Alerts" style={inp} />
            </div>
            <div>
              <label style={{ display:'block', fontSize:'10px', color:'#64748b', marginBottom:'4px', textTransform:'uppercase', fontWeight:600 }}>Webhook URL</label>
              <input value={newUrl} onChange={e=>setNewUrl(e.target.value)} placeholder="https://..." style={inp} />
            </div>
          </div>
          <div style={{ marginBottom:'12px' }}>
            <label style={{ display:'block', fontSize:'10px', color:'#64748b', marginBottom:'6px', textTransform:'uppercase', fontWeight:600 }}>Events to Subscribe</label>
            <div style={{ display:'flex', flexWrap:'wrap' as const, gap:'6px' }}>
              {ALL_EVENTS.map(ev => (
                <button key={ev} onClick={() => setNewEvents(prev => prev.includes(ev) ? prev.filter(x=>x!==ev) : [...prev, ev])}
                  style={{ padding:'3px 10px', borderRadius:'6px', border:`1px solid ${newEvents.includes(ev)?'rgba(59,130,246,0.5)':'rgba(255,255,255,0.08)'}`, background:newEvents.includes(ev)?'rgba(59,130,246,0.15)':'transparent', color:newEvents.includes(ev)?'#60a5fa':'#64748b', fontSize:'10px', fontWeight:600, cursor:'pointer' }}>
                  {ev}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display:'flex', gap:'8px' }}>
            <button onClick={() => setShowNew(false)} style={{ flex:1, padding:'8px', borderRadius:'7px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#64748b', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>Cancel</button>
            <button onClick={createHook} disabled={saving || !newUrl.trim()} style={{ flex:2, padding:'8px', borderRadius:'7px', border:'none', background:saving||!newUrl.trim()?'rgba(59,130,246,0.3)':'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontSize:'12px', fontWeight:700, cursor:saving||!newUrl.trim()?'not-allowed':'pointer' }}>
              {saving ? 'Creating…' : 'Create Webhook'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Branding Tab ─────────────────────────────────────────────────────────────
function BrandingTab() {
  const { theme, updateTheme } = useTheme();
  const { toast } = useToast();
  const [logoUrl, setLogoUrl] = useState(theme.logoUrl || '');
  const [orgName, setOrgName] = useState(theme.orgNameOverride || '');
  const [logoText, setLogoText] = useState(theme.logoText || 'L');
  const [primaryColor, setPrimaryColor] = useState(theme.primaryColor || '#3b82f6');
  const [saved, setSaved] = useState(false);

  function handleSave() {
    updateTheme({ logoUrl: logoUrl.trim(), orgNameOverride: orgName.trim(), logoText: logoText.trim() || 'L', primaryColor });
    localStorage.setItem('lemai_org_name', orgName.trim());
    setSaved(true);
    toast('Branding saved', 'success');
    setTimeout(() => setSaved(false), 2500);
  }

  const inp: React.CSSProperties = { width:'100%', boxSizing:'border-box', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', padding:'9px 12px', color:'white', fontSize:'13px', fontFamily:'inherit', outline:'none' };

  return (
    <div style={{ padding:'24px', maxWidth:'600px' }}>
      <div style={{ fontSize:'13px', fontWeight:600, color:'#e2e8f0', marginBottom:'4px' }}>Organisation Branding</div>
      <div style={{ fontSize:'12px', color:'#64748b', marginBottom:'24px' }}>Customise the platform name, logo and accent colour. Changes apply immediately across all pages.</div>

      {/* Logo preview */}
      <div style={{ display:'flex', alignItems:'center', gap:'16px', padding:'16px', background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', marginBottom:'24px' }}>
        {logoUrl
          ? <img src={logoUrl} alt="logo preview" style={{ width:'44px', height:'44px', borderRadius:'10px', objectFit:'contain', background:'rgba(255,255,255,0.06)', padding:'4px' }} onError={e => { (e.currentTarget as HTMLImageElement).style.opacity = '0.3'; }} />
          : <div style={{ width:'44px', height:'44px', borderRadius:'10px', background:`linear-gradient(135deg,${primaryColor},#1d4ed8)`, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, fontSize:'20px', color:'white' }}>{logoText || 'L'}</div>
        }
        <div>
          <div style={{ fontWeight:700, fontSize:'15px', color:'white' }}>{orgName || 'lemai'}</div>
          <div style={{ fontSize:'10px', color:'#475569' }}>Intelligence Platform</div>
        </div>
        <div style={{ marginLeft:'auto', fontSize:'10px', color:'#334155' }}>Live preview</div>
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:'16px' }}>
        <div>
          <label style={{ display:'block', fontSize:'11px', color:'#64748b', marginBottom:'6px', fontWeight:600 }}>ORGANISATION NAME</label>
          <input value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="e.g. City of Parramatta" style={inp} />
          <div style={{ fontSize:'10px', color:'#475569', marginTop:'4px' }}>Shown in the sidebar and on exported PDFs</div>
        </div>

        <div>
          <label style={{ display:'block', fontSize:'11px', color:'#64748b', marginBottom:'6px', fontWeight:600 }}>LOGO URL</label>
          <input value={logoUrl} onChange={e => setLogoUrl(e.target.value)} placeholder="https://example.com/logo.png" style={inp} />
          <div style={{ fontSize:'10px', color:'#475569', marginTop:'4px' }}>Enter the full URL of your organisation logo (PNG or SVG recommended). Leave blank to use the lettermark.</div>
        </div>

        <div>
          <label style={{ display:'block', fontSize:'11px', color:'#64748b', marginBottom:'6px', fontWeight:600 }}>LETTERMARK (fallback when no logo URL)</label>
          <input value={logoText} onChange={e => setLogoText(e.target.value)} placeholder="L" maxLength={2} style={{ ...inp, width:'80px', textAlign:'center', fontSize:'20px', fontWeight:800 }} />
        </div>

        <div>
          <label style={{ display:'block', fontSize:'11px', color:'#64748b', marginBottom:'6px', fontWeight:600 }}>ACCENT COLOUR</label>
          <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
            <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} style={{ width:'44px', height:'44px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.1)', background:'none', cursor:'pointer', padding:'2px' }} />
            <input value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} placeholder="#3b82f6" style={{ ...inp, width:'140px' }} />
            <div style={{ display:'flex', gap:'8px' }}>
              {['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4'].map(c => (
                <div key={c} onClick={() => setPrimaryColor(c)} style={{ width:'24px', height:'24px', borderRadius:'50%', background:c, cursor:'pointer', border: primaryColor===c ? '2px solid white' : '2px solid transparent', transition:'border 0.1s' }} />
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ marginTop:'24px' }}>
        <button onClick={handleSave} style={{ padding:'10px 28px', borderRadius:'8px', border:'none', background: saved ? 'rgba(34,197,94,0.15)' : 'linear-gradient(135deg,#3b82f6,#1d4ed8)', color: saved ? '#22c55e' : 'white', fontWeight:700, fontSize:'13px', cursor:'pointer', transition:'all 0.2s' }}>
          {saved ? '✓ Branding Saved' : 'Save Branding'}
        </button>
      </div>
    </div>
  );
}

export default function Settings() {
  const { toast } = useToast();
  const { isSuperAdmin } = useLicense();
  const [tab, setTab]                   = useState<Tab>('cameras');
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setInitialLoading(false), 650);
    return () => clearTimeout(t);
  }, []);
  useTheme(); // keep ThemeContext connected (reads dark mode vars)
  const [cameras, setCameras]           = useState<Camera[]>(SEED_CAMERAS);
  const [integrations, setIntegrations] = useState<IntegrationConfig[]>(SEED_INTEGRATIONS);
  const [showAddCam, setShowAddCam]     = useState(false);

  // ── Load cameras + integrations from backend ──────────────────────────────
  useEffect(() => {
    const tok = localStorage.getItem('lemai_token') || '';
    if (!tok) return;
    const hdrs = { Authorization: `Bearer ${tok}` };
    Promise.all([
      fetch('/api/integrations', { headers: hdrs }).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/integrations/cameras', { headers: hdrs }).then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([intData, camData]) => {
      if (intData?.integrations?.length) setIntegrations(intData.integrations);
      if (camData?.cameras?.length)       setCameras(camData.cameras);
    });
  }, []);

  // ── Controllers (Protege GX) ──
  const [controllers, setControllers]   = useState<CtlDevice[]>(SEED_CONTROLLERS);
  const [ctlDoors, setCtlDoors]         = useState<CtlDoor[]>(SEED_CTL_DOORS);
  const [ctlExpanded, setCtlExpanded]   = useState<Record<string, boolean>>({});
  const [ctlSiteFilter, setCtlSiteFilter] = useState('All');
  const [ctlLoading, setCtlLoading]     = useState(false);

  useEffect(() => {
    const tok = localStorage.getItem('lemai_token') || '';
    if (!tok) return;
    setCtlLoading(true);
    Promise.all([
      fetch('/api/protege/controllers', { headers: { Authorization: `Bearer ${tok}` } }).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch('/api/protege/doors', { headers: { Authorization: `Bearer ${tok}` } }).then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([cData, dData]) => {
      if (cData?.controllers?.length) {
        setControllers(cData.controllers.map((c: any) => ({
          id: c.id, name: c.name, site: c.siteId || '', ip: '', doors: 0, readers: 0,
          status: c.online ? 'online' : 'offline', firmware: '', lastSeen: c.online ? 'Live' : 'Offline', health: c.online ? 'OK' : 'Offline',
        })));
      }
      if (dData?.doors?.length) {
        setCtlDoors(dData.doors.map((d: any) => ({
          id: d.id, name: d.name, controller: d.controller || d.controllerId || '',
          controllerId: d.controllerId || '', status: d.status || 'unknown',
        })));
      }
    }).finally(() => setCtlLoading(false));
  }, []);

  // ── Alarm Zones (ArmorIP) for Camera Linking ──
  const [armorZones, setArmorZones]     = useState<ArmorZone[]>([]);
  const [zoneFilter, setZoneFilter]     = useState('All');
  const [selectedZoneKey, setSelectedZoneKey] = useState<string | null>(null);
  const [zoneCamLinks, setZoneCamLinks] = useState<Record<string, ZoneCameraLink>>(() => {
    try { return JSON.parse(localStorage.getItem('lemai_zone_cameras') || '{}'); } catch { return {}; }
  });
  const [showCamPicker, setShowCamPicker] = useState(false);
  const [manualCamName, setManualCamName] = useState('');
  const [manualCamDesc, setManualCamDesc] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [addingManualZone, setAddingManualZone] = useState(false);
  const [newZoneSite, setNewZoneSite]   = useState(SITES_LIST[0]);
  const [newZoneLoc, setNewZoneLoc]     = useState('');

  useEffect(() => {
    const tok = localStorage.getItem('lemai_token') || '';
    if (!tok) return;
    fetch('/api/armorip/zones', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.zones?.length) setArmorZones(d.zones); })
      .catch(() => {});
  }, []);

  const saveZoneCamLinks = (updated: Record<string, ZoneCameraLink>) => {
    setZoneCamLinks(updated);
    localStorage.setItem('lemai_zone_cameras', JSON.stringify(updated));
    toast('Camera links saved', 'success');
  };

  const getZoneLink = (key: string): ZoneCameraLink =>
    zoneCamLinks[key] || { cameras: [], manualCameras: [] };

  const addCamToZone = (key: string, camName: string) => {
    const link = getZoneLink(key);
    if (link.cameras.includes(camName) || link.cameras.length + link.manualCameras.length >= 4) return;
    saveZoneCamLinks({ ...zoneCamLinks, [key]: { ...link, cameras: [...link.cameras, camName] } });
  };

  const removeCamFromZone = (key: string, camName: string) => {
    const link = getZoneLink(key);
    saveZoneCamLinks({ ...zoneCamLinks, [key]: { ...link, cameras: link.cameras.filter(c => c !== camName) } });
  };

  const addManualCamToZone = (key: string) => {
    if (!manualCamName.trim()) return;
    const link = getZoneLink(key);
    if (link.cameras.length + link.manualCameras.length >= 4) { toast('Maximum 4 cameras per zone', 'error'); return; }
    saveZoneCamLinks({ ...zoneCamLinks, [key]: { ...link, manualCameras: [...link.manualCameras, { name: manualCamName.trim(), description: manualCamDesc.trim() }] } });
    setManualCamName(''); setManualCamDesc('');
  };

  const removeManualCamFromZone = (key: string, idx: number) => {
    const link = getZoneLink(key);
    saveZoneCamLinks({ ...zoneCamLinks, [key]: { ...link, manualCameras: link.manualCameras.filter((_, i) => i !== idx) } });
  };
  const [showAddInt, setShowAddInt]     = useState(false);
  const [camSearch, setCamSearch]       = useState('');
  const [camSite, setCamSite]           = useState('All');
  const [selectedCam, setSelectedCam]   = useState<Camera | null>(null);
  const [deletingCam, setDeletingCam]   = useState<number | null>(null);
  // SOP Manager
  const [sops, setSops]                 = useState<Record<string, string[]>>({ ...DEFAULT_SOPS });
  const [selectedAlarmType, setSelectedAlarmType] = useState<string>(SOP_ALARM_TYPES[0]);
  const [sopDraft, setSopDraft]         = useState<string[]>(DEFAULT_SOPS[SOP_ALARM_TYPES[0]]);
  const [sopSaved, setSopSaved]         = useState(false);
  // Alarm Types
  const [alarmTypesConfig, setAlarmTypesConfig] = useState<Record<string, AlarmTypeConfig>>({});
  const [selectedTypeForEdit, setSelectedTypeForEdit] = useState<string>(SOP_ALARM_TYPES[0]);
  const [alarmTypeDraft, setAlarmTypeDraft] = useState<AlarmTypeConfig>({ displayName: SOP_ALARM_TYPES[0], category: 'Security', sop: [], aliases: [] });
  const [alarmTypeSaved, setAlarmTypeSaved] = useState(false);
  const [alarmAliasInput, setAlarmAliasInput] = useState('');
  const [seenTypes, setSeenTypes] = useState<{ raw: string; mappedTo: string | null }[]>([]);
  // Escalation
  const [escalation, setEscalation]     = useState<EscalationRule[]>(SEED_ESCALATION);
  const [escThresholds, setEscThresholds] = useState([
    { level: 'escalated_l1', minutes: 5,  label: 'No ACK in 5 minutes',  description: 'Level 1 — Operator alert' },
    { level: 'escalated_l2', minutes: 15, label: 'No ACK in 15 minutes', description: 'Level 2 — Supervisor escalation' },
    { level: 'escalated_l3', minutes: 30, label: 'No ACK in 30 minutes', description: 'Level 3 — Senior supervisor' },
    { level: 'escalated_l4', minutes: 60, label: 'No ACK in 60 minutes', description: 'Level 4 — Management escalation' },
  ]);
  const [escSaving, setEscSaving] = useState(false);
  const [escSaved, setEscSaved]   = useState(false);
  useEffect(() => {
    if (tab !== 'escalation') return;
    const tok = localStorage.getItem('lemai_token') || '';
    if (!tok) return;
    fetch('/api/armorip/escalation-config', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.json())
      .then(d => { if (d.thresholds) setEscThresholds(d.thresholds); })
      .catch(() => {});
  }, [tab]);

  // VMS / camera stream URLs (persisted to localStorage)
  const [vmsUrl, setVmsUrl] = useState<string>(() => localStorage.getItem('lemai_vms_url') || '');
  const [camStreams, setCamStreams] = useState<Record<string,string>>(() => {
    try { return JSON.parse(localStorage.getItem('lemai_cam_streams') || '{}'); } catch { return {}; }
  });
  const [editingCamStream, setEditingCamStream] = useState<string>('');

  // ArmorIP — alarm-type camera linking (persisted to localStorage)
  const [alarmCamConfig, setAlarmCamConfig] = useState<Record<string,string[]>>(() => {
    try { return JSON.parse(localStorage.getItem('lemai_alarm_type_cameras') || '{}'); } catch { return {}; }
  });
  const [armorTestSent,  setArmorTestSent]  = useState(false);
  const [armorTestType,  setArmorTestType]  = useState('Motion Detected');
  const [armorCamDraft,  setArmorCamDraft]  = useState<Record<string,string>>({});  // per-type text input draft
  const [armorWebhookUrl, setArmorWebhookUrl] = useState('');

  // Resolve webhook URL on mount
  useEffect(() => {
    const proto = window.location.protocol;
    const host  = window.location.host;
    setArmorWebhookUrl(`${proto}//${host}/api/armorip/webhook`);
  }, []);

  // Load alarm type display config + seen incoming types from backend
  useEffect(() => {
    const tok = localStorage.getItem('lemai_token') || '';
    if (!tok) return;
    fetch('/api/armorip/alarm-types', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.config) setAlarmTypesConfig(data.config); })
      .catch(() => {});
    fetch('/api/armorip/seen-types', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.types) setSeenTypes(data.types); })
      .catch(() => {});
  }, []);

  const saveAlarmCamConfig = (cfg: Record<string,string[]>) => {
    setAlarmCamConfig(cfg);
    localStorage.setItem('lemai_alarm_type_cameras', JSON.stringify(cfg));
    const token = localStorage.getItem('lemai_token') || sessionStorage.getItem('lemai_token');
    fetch('/api/integrations/cameras/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ alarm_cam_config: cfg }),
    }).catch(() => {});
    toast('Camera links saved', 'success');
  };

  const saveAlarmTypesConfig = async (cfg: Record<string, AlarmTypeConfig>) => {
    const tok = localStorage.getItem('lemai_token') || '';
    try {
      await fetch('/api/armorip/alarm-types', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tok}` },
        body: JSON.stringify({ config: cfg }),
      });
      setAlarmTypesConfig(cfg);
      // Refresh seen types — some may now show as mapped
      fetch('/api/armorip/seen-types', { headers: { Authorization: `Bearer ${tok}` } })
        .then(r => r.ok ? r.json() : null)
        .then(data => { if (data?.types) setSeenTypes(data.types); })
        .catch(() => {});
      toast('Alarm type configuration saved', 'success');
    } catch {
      toast('Failed to save alarm types — check backend', 'error');
    }
  };

  const addCameraToType = (alarmType: string) => {
    const raw = (armorCamDraft[alarmType] || '').trim();
    if (!raw) return;
    const cams = raw.split(',').map(s => s.trim()).filter(Boolean);
    const existing = alarmCamConfig[alarmType] || [];
    const seenM: Record<string,boolean> = {};
    const merged = [...existing, ...cams].filter(c => seenM[c] ? false : (seenM[c] = true));
    saveAlarmCamConfig({ ...alarmCamConfig, [alarmType]: merged });
    setArmorCamDraft(d => ({ ...d, [alarmType]: '' }));
  };

  const removeCameraFromType = (alarmType: string, cam: string) => {
    const updated = (alarmCamConfig[alarmType] || []).filter(c => c !== cam);
    saveAlarmCamConfig({ ...alarmCamConfig, [alarmType]: updated });
  };

  const sendTestAlarm = async () => {
    const tok = localStorage.getItem('lemai_token') || '';
    try {
      await fetch('/api/armorip/test', {
        method:'POST', headers:{'Content-Type':'application/json','Authorization':`Bearer ${tok}`},
        body: JSON.stringify({ type: armorTestType, site: 'Test Site', location: 'Test Location', severity: 'warning' }),
      });
      setArmorTestSent(true);
      toast(`Test alarm "${armorTestType}" sent to SOC Dashboard`, 'success');
      setTimeout(() => setArmorTestSent(false), 3000);
    } catch { toast('Test alarm failed — check backend', 'error'); }
  };

  let idRef = { current: 100 };

  const handleAddCamera = useCallback(async (data: typeof BLANK_CAM) => {
    const tok = localStorage.getItem('lemai_token') || '';
    try {
      const res = await fetch('/api/integrations/cameras', {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        const saved = await res.json();
        setCameras(prev => [saved.camera, ...prev]);
      } else {
        throw new Error('API error');
      }
    } catch {
      // Optimistic fallback
      const newCam: Camera = { ...data, id: Date.now(), addedDate: new Date().toISOString().split('T')[0] } as Camera;
      setCameras(prev => [newCam, ...prev]);
    }
    toast(`Camera added: ${data.name}`, 'success');
  }, [toast]);

  const handleAddIntegration = useCallback(async (data: typeof BLANK_INT) => {
    const tok = localStorage.getItem('lemai_token') || '';
    try {
      const res = await fetch('/api/integrations', {
        method: 'POST',
        headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (res.ok) {
        const saved = await res.json();
        setIntegrations(prev => [saved.integration, ...prev]);
      } else {
        throw new Error('API error');
      }
    } catch {
      const newInt: IntegrationConfig = { ...data, id: Date.now(), status: 'Pending', lastSync: 'Never', icon: '🔌' };
      setIntegrations(prev => [newInt, ...prev]);
    }
    toast(`Integration added: ${data.name}`, 'success');
  }, [toast]);

  const handleDeleteCam = useCallback((id: number) => {
    const cam = cameras.find(c => c.id === id);
    setDeletingCam(id);
    const tok = localStorage.getItem('lemai_token') || '';
    fetch(`/api/integrations/cameras/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${tok}` },
    }).catch(() => {});
    setTimeout(() => {
      setCameras(prev => prev.filter(c => c.id !== id));
      setDeletingCam(null);
      setSelectedCam(null);
    }, 600);
    if (cam) toast(`Camera removed: ${cam.name}`, 'warning');
  }, [cameras, toast]);

  const filteredCams = cameras.filter(c => {
    const matchesSite   = camSite === 'All' || c.site === camSite;
    const matchesSearch = !camSearch || c.name.toLowerCase().includes(camSearch.toLowerCase()) || c.ip.includes(camSearch) || c.cameraId.toLowerCase().includes(camSearch.toLowerCase());
    return matchesSite && matchesSearch;
  });

  const tabStyle = (t: Tab): React.CSSProperties => ({
    padding:'8px 18px', border:'none', background:'none', cursor:'pointer', fontSize:'12px', fontWeight:600,
    color: tab === t ? '#e2e8f0' : '#64748b',
    borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
    transition:'all 0.15s',
  });

  // Counts
  const onlineCams   = cameras.filter(c => c.status === 'Online').length;
  const connectedInt = integrations.filter(i => i.status === 'Connected').length;

  return (
    <Layout title="Settings" subtitle="System configuration — cameras, integrations, sites and preferences">

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'20px' }}>
        {[
          { l:'Total Cameras',     v: String(cameras.length),      c:'#e2e8f0', icon:'📹', sub:`${onlineCams} online` },
          { l:'Integrations',      v: String(integrations.length), c:'#3b82f6', icon:'🔌', sub:`${connectedInt} connected` },
          { l:'Controllers',       v: String(controllers.length),  c:'#22c55e', icon:'🔐', sub:`${controllers.filter(c=>c.status==='online').length} online` },
          { l:'System Health',     v:'Healthy',                    c:'#22c55e', icon:'💚', sub:'All services running' },
        ].map(s => (
          <div key={s.l} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', padding:'16px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px' }}>
              <span style={{ fontSize:'11px', color:'#64748b' }}>{s.l}</span>
              <span style={{ fontSize:'18px' }}>{s.icon}</span>
            </div>
            <div style={{ fontSize:'22px', fontWeight:700, color:s.c }}>{s.v}</div>
            <div style={{ fontSize:'10px', color:'#475569', marginTop:'3px' }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Tab container */}
      <div style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'12px', overflow:'hidden' }}>
        {/* Tab bar */}
        <div style={{ padding:'0 20px', borderBottom:'1px solid rgba(255,255,255,0.06)', display:'flex', gap:'0' }}>
          {([
            ['cameras','Cameras'],
            ['integrations','Integrations'],
            ['controllers','Controllers'],
            ['camera-linking','Camera Linking'],
            ['armorip','⚡ Alarm Webhook'],
            ['sop-manager','SOP Manager'],
            ['alarm-types','🏷 Alarm Types'],
            ['escalation','Escalation'],
            ['alarm-notifications','🔔 Alarm Notifications'],
            ['security','🔐 Security & MFA'],
            ['sessions','🖥 Active Sessions'],
            ['notifications','📣 Notifications'],
            ['oncall','📞 On-Call'],
            ['email','✉ Email'],
            ['branding','Branding'],
            ...(isSuperAdmin ? [['system','System']] : []),
          ] as [Tab, string][]).map(([t, label]) => (
            <button key={t} style={tabStyle(t as Tab)} onClick={() => setTab(t as Tab)}>
              {label}
            </button>
          ))}
        </div>

        {/* ── Cameras Tab ──────────────────────────────────────────────── */}
        {tab === 'cameras' && (
          <div style={{ display:'flex', height:'calc(100vh - 340px)', minHeight:'400px' }}>
            {/* Left: camera list */}
            <div style={{ width:'55%', borderRight:'1px solid rgba(255,255,255,0.06)', display:'flex', flexDirection:'column' }}>
              {/* Toolbar */}
              <div style={{ padding:'12px 16px', borderBottom:'1px solid rgba(255,255,255,0.04)', display:'flex', gap:'8px', alignItems:'center' }}>
                <input value={camSearch} onChange={e => setCamSearch(e.target.value)} placeholder="Search cameras..."
                  style={{ flex:1, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'7px', padding:'6px 10px', color:'#e2e8f0', fontSize:'11px', outline:'none' }} />
                <select value={camSite} onChange={e => setCamSite(e.target.value)}
                  style={{ background:'#1e293b', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'7px', padding:'6px 10px', color:'#64748b', fontSize:'11px', outline:'none' }}>
                  <option>All</option>
                  {SITES_LIST.map(s => <option key={s}>{s}</option>)}
                </select>
                <button onClick={() => setShowAddCam(true)} style={{ padding:'6px 14px', borderRadius:'7px', border:'none', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontSize:'11px', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                  + Add Camera
                </button>
              </div>
              {/* List */}
              <div style={{ overflowY:'auto', flex:1 }}>
                {initialLoading && Array.from({length:6}).map((_,i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 16px', borderBottom:'1px solid rgba(255,255,255,0.03)', borderLeft:'3px solid transparent' }}>
                    <div className="skeleton" style={{ width:'8px', height:'8px', borderRadius:'50%', flexShrink:0 }} />
                    <div style={{ flex:1 }}>
                      <div className="skeleton" style={{ height:'12px', width:'120px', marginBottom:'5px' }} />
                      <div className="skeleton" style={{ height:'10px', width:'150px' }} />
                    </div>
                    <div className="skeleton" style={{ height:'18px', width:'50px', borderRadius:'10px' }} />
                  </div>
                ))}
                {!initialLoading && filteredCams.length === 0 && (
                  <div style={{ padding:'40px', textAlign:'center', color:'#334155', fontSize:'13px' }}>No cameras match your filters</div>
                )}
                {!initialLoading && filteredCams.map(cam => (
                  <div key={cam.id} onClick={() => setSelectedCam(cam)} style={{
                    display:'flex', alignItems:'center', gap:'12px', padding:'10px 16px',
                    borderBottom:'1px solid rgba(255,255,255,0.03)',
                    background: selectedCam?.id === cam.id ? 'rgba(59,130,246,0.08)' : deletingCam === cam.id ? 'rgba(239,68,68,0.05)' : 'transparent',
                    borderLeft: selectedCam?.id === cam.id ? '3px solid #3b82f6' : '3px solid transparent',
                    cursor:'pointer', transition:'all 0.15s',
                  }}>
                    <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:STATUS_COLOR[cam.status], flexShrink:0, boxShadow:`0 0 5px ${STATUS_COLOR[cam.status]}60` }} />
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:'12px', fontWeight:600, color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{cam.name}</div>
                      <div style={{ fontSize:'10px', color:'#475569' }}>{cam.site} · {cam.location}</div>
                    </div>
                    <div style={{ textAlign:'right', flexShrink:0 }}>
                      <div style={{ fontSize:'10px', color:'#475569', fontFamily:'monospace' }}>{cam.ip}</div>
                      <div style={{ fontSize:'9px', color:STATUS_COLOR[cam.status], fontWeight:600 }}>{cam.status}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ padding:'8px 16px', borderTop:'1px solid rgba(255,255,255,0.04)', fontSize:'10px', color:'#334155' }}>
                {filteredCams.length} of {cameras.length} cameras
              </div>
            </div>

            {/* Right: camera detail */}
            <div style={{ flex:1, padding:'20px', overflowY:'auto' }}>
              {!selectedCam ? (
                <div style={{ height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', color:'#334155', gap:'12px' }}>
                  <div style={{ fontSize:'32px' }}>📹</div>
                  <div style={{ fontSize:'13px' }}>Select a camera to view details</div>
                  <div style={{ fontSize:'11px' }}>or click <strong style={{ color:'#3b82f6' }}>+ Add Camera</strong> to register a new one</div>
                </div>
              ) : (
                <div>
                  {/* Camera detail header */}
                  <div style={{ marginBottom:'16px' }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                      <div>
                        <h3 style={{ margin:0, fontSize:'16px', fontWeight:700, color:'#e2e8f0' }}>{selectedCam.name}</h3>
                        <p style={{ margin:'4px 0 0', fontSize:'12px', color:'#64748b' }}>{selectedCam.cameraId} · {selectedCam.site}</p>
                      </div>
                      <div style={{ fontSize:'10px', fontWeight:700, padding:'3px 10px', borderRadius:'8px', background:`${STATUS_COLOR[selectedCam.status]}15`, color:STATUS_COLOR[selectedCam.status] }}>
                        {selectedCam.status}
                      </div>
                    </div>
                  </div>

                  {/* Camera stream preview / config */}
                  {(() => {
                    const streamUrl = camStreams[selectedCam.name] || '';
                    const isImg = streamUrl && /\.(jpg|jpeg|png|cgi)(\?|$)/i.test(streamUrl);
                    return (
                      <div style={{ background:'rgba(0,0,0,0.4)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', marginBottom:'16px', overflow:'hidden' }}>
                        {streamUrl ? (
                          isImg ? (
                            <img src={streamUrl} alt={selectedCam.name} style={{ width:'100%', height:'140px', objectFit:'cover', display:'block' }}
                              onError={e => { (e.target as HTMLImageElement).style.display='none'; }}
                            />
                          ) : (
                            <iframe src={streamUrl} title={selectedCam.name} style={{ width:'100%', height:'140px', border:'none', display:'block' }} />
                          )
                        ) : (
                          <div style={{ height:'140px', display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:'6px' }}>
                            <div style={{ fontSize:'24px' }}>📷</div>
                            <div style={{ fontSize:'11px', color:'#475569' }}>No stream URL configured</div>
                            <div style={{ fontSize:'10px', color:'#334155' }}>Add a stream URL below to enable live preview</div>
                          </div>
                        )}
                        <div style={{ padding:'10px 12px', borderTop:'1px solid rgba(255,255,255,0.04)', background:'rgba(0,0,0,0.2)' }}>
                          <label style={{ display:'block', fontSize:'9px', color:'#475569', fontWeight:700, textTransform:'uppercase', marginBottom:'5px', letterSpacing:'0.5px' }}>
                            Live Stream URL (MJPEG, RTSP snapshot, or web interface URL)
                          </label>
                          <div style={{ display:'flex', gap:'6px' }}>
                            <input
                              value={editingCamStream !== '' ? editingCamStream : (streamUrl || '')}
                              onChange={e => setEditingCamStream(e.target.value)}
                              onFocus={() => setEditingCamStream(streamUrl || '')}
                              placeholder={`http://${selectedCam.ip}/cgi-bin/snapshot.cgi`}
                              style={{ flex:1, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'5px 8px', color:'#e2e8f0', fontSize:'11px', outline:'none' }}
                            />
                            <button onClick={() => {
                              const url = editingCamStream.trim();
                              const updated = { ...camStreams, [selectedCam.name]: url };
                              setCamStreams(updated);
                              localStorage.setItem('lemai_cam_streams', JSON.stringify(updated));
                              setEditingCamStream('');
                              toast(`Stream URL saved for ${selectedCam.name}`, 'success');
                            }} style={{ padding:'5px 12px', background:'rgba(59,130,246,0.15)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:'6px', color:'#3b82f6', fontSize:'10px', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                              Save
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* VMS base URL */}
                  <div style={{ marginBottom:'14px', padding:'10px 12px', background:'rgba(59,130,246,0.04)', border:'1px solid rgba(59,130,246,0.12)', borderRadius:'8px' }}>
                    <label style={{ display:'block', fontSize:'9px', color:'#3b82f6', fontWeight:700, textTransform:'uppercase', marginBottom:'6px', letterSpacing:'0.5px' }}>
                      VMS / NVR Base URL (Milestone, Dahua, Genetec — applies to all cameras)
                    </label>
                    <div style={{ display:'flex', gap:'6px' }}>
                      <input
                        value={vmsUrl}
                        onChange={e => setVmsUrl(e.target.value)}
                        placeholder="http://milestone.yourdomain.com:8080 or http://192.168.1.1"
                        style={{ flex:1, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'5px 8px', color:'#e2e8f0', fontSize:'11px', outline:'none' }}
                      />
                      <button onClick={() => {
                        localStorage.setItem('lemai_vms_url', vmsUrl.trim());
                        toast('VMS URL saved', 'success');
                      }} style={{ padding:'5px 12px', background:'rgba(59,130,246,0.15)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:'6px', color:'#3b82f6', fontSize:'10px', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                        Save
                      </button>
                    </div>
                    {vmsUrl && <div style={{ marginTop:'5px', fontSize:'9px', color:'#475569' }}>
                      "Open in VMS" buttons will link to: <span style={{ color:'#60a5fa', fontFamily:'monospace' }}>{vmsUrl}</span>
                    </div>}
                  </div>

                  {/* Fields */}
                  {[
                    ['Camera ID',    selectedCam.cameraId],
                    ['IP Address',   selectedCam.ip],
                    ['Site',         selectedCam.site],
                    ['Location',     selectedCam.location],
                    ['Type',         selectedCam.type],
                    ['Resolution',   selectedCam.resolution],
                    ['Manufacturer', selectedCam.manufacturer],
                    ['Model',        selectedCam.model],
                    ['Added',        selectedCam.addedDate],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ fontSize:'11px', color:'#475569' }}>{k}</span>
                      <span style={{ fontSize:'11px', color:'#e2e8f0', fontWeight:500 }}>{v}</span>
                    </div>
                  ))}

                  {/* Actions */}
                  <div style={{ display:'flex', gap:'8px', marginTop:'16px' }}>
                    {vmsUrl && <button onClick={() => window.open(vmsUrl, '_blank')} style={{ flex:1, padding:'8px', borderRadius:'7px', border:'1px solid rgba(59,130,246,0.3)', background:'rgba(59,130,246,0.1)', color:'#3b82f6', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                      Open in VMS →
                    </button>}
                    <button onClick={() => handleDeleteCam(selectedCam.id)} style={{ flex:1, padding:'8px', borderRadius:'7px', border:'1px solid rgba(239,68,68,0.3)', background:'rgba(239,68,68,0.08)', color:'#ef4444', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                      {deletingCam === selectedCam.id ? 'Removing...' : 'Remove Camera'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Integrations Tab ─────────────────────────────────────────── */}
        {tab === 'integrations' && (
          <div>
            {/* Toolbar */}
            <div style={{ padding:'12px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <span style={{ fontSize:'12px', color:'#64748b' }}>{integrations.length} integrations configured</span>
              <button onClick={() => setShowAddInt(true)} style={{ padding:'6px 16px', borderRadius:'7px', border:'none', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontSize:'11px', fontWeight:700, cursor:'pointer' }}>
                + Add Integration
              </button>
            </div>
            <div>
              {integrations.map(intg => (
                <div key={intg.id} style={{ display:'flex', alignItems:'center', gap:'14px', padding:'14px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)', transition:'background 0.15s' }}
                  onMouseOver={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.01)')}
                  onMouseOut={e => (e.currentTarget.style.background = 'transparent')}
                >
                  {/* Icon */}
                  <div style={{ width:'40px', height:'40px', borderRadius:'10px', background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px', flexShrink:0 }}>
                    {intg.icon}
                  </div>
                  {/* Info */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'2px' }}>
                      <span style={{ fontSize:'13px', fontWeight:600, color:'#e2e8f0' }}>{intg.name}</span>
                      <span style={{ fontSize:'9px', padding:'1px 6px', borderRadius:'5px', background:'rgba(100,116,139,0.15)', color:'#64748b', fontWeight:600 }}>{intg.type}</span>
                    </div>
                    <div style={{ fontSize:'11px', color:'#475569', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{intg.description}</div>
                    <div style={{ fontSize:'10px', color:'#334155', marginTop:'2px' }}>{intg.vendor} · {intg.host}{intg.port ? ':' + intg.port : ''}</div>
                  </div>
                  {/* Status + sync */}
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontSize:'10px', fontWeight:700, padding:'2px 8px', borderRadius:'8px', background:`${STATUS_COLOR[intg.status]}15`, color:STATUS_COLOR[intg.status], marginBottom:'4px' }}>
                      {intg.status}
                    </div>
                    <div style={{ fontSize:'9px', color:'#334155' }}>Sync: {intg.lastSync}</div>
                  </div>
                  {/* Actions */}
                  <div style={{ display:'flex', flexDirection:'column', gap:'4px', flexShrink:0 }}>
                    <button style={{ padding:'4px 12px', borderRadius:'6px', border:'1px solid rgba(59,130,246,0.2)', background:'rgba(59,130,246,0.08)', color:'#3b82f6', fontSize:'10px', fontWeight:600, cursor:'pointer' }}>Configure</button>
                    <button style={{ padding:'4px 12px', borderRadius:'6px', border:'1px solid rgba(255,255,255,0.06)', background:'transparent', color:'#64748b', fontSize:'10px', fontWeight:600, cursor:'pointer' }}>Test</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Controllers Tab (live Protege GX) ───────────────────────── */}
        {tab === 'controllers' && (() => {
          const sitesInCtl = ['All', ...Array.from(new Set(controllers.map(c => c.site).filter(Boolean)))];
          const filteredCtl = ctlSiteFilter === 'All' ? controllers : controllers.filter(c => c.site === ctlSiteFilter);
          return (
            <div>
              <div style={{ padding:'12px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)', display:'flex', alignItems:'center', gap:'12px' }}>
                <select value={ctlSiteFilter} onChange={e => setCtlSiteFilter(e.target.value)}
                  style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'5px 10px', color:'#e2e8f0', fontSize:'11px', cursor:'pointer' }}>
                  {sitesInCtl.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <span style={{ fontSize:'11px', color:'#475569' }}>{filteredCtl.length} controller{filteredCtl.length !== 1 ? 's' : ''}</span>
                {ctlLoading && <span style={{ fontSize:'10px', color:'#64748b' }}>Syncing with Protege GX…</span>}
                <div style={{ marginLeft:'auto', display:'flex', gap:'8px', alignItems:'center' }}>
                  <span style={{ fontSize:'10px', color:'#22c55e' }}>● Source: ICT Protege GX</span>
                  <a href="/m/access" style={{ fontSize:'11px', color:'#3b82f6', fontWeight:600, textDecoration:'none' }}>Open Access Control →</a>
                </div>
              </div>
              <div style={{ padding:'16px 20px', display:'flex', flexDirection:'column', gap:'8px', overflowY:'auto', maxHeight:'calc(100vh - 380px)' }}>
                {filteredCtl.map(ctl => {
                  const doors = ctlDoors.filter(d => d.controller === ctl.id || d.controllerId === ctl.id);
                  const isOpen = !!ctlExpanded[ctl.id];
                  const doorStatusColor = (st: string) =>
                    st === 'locked' ? '#64748b' : st === 'unlocked' ? '#22c55e' : st === 'open' ? '#3b82f6' : st === 'forced-open' || st === 'forced' ? '#ef4444' : '#f59e0b';
                  return (
                    <div key={ctl.id} style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', overflow:'hidden' }}>
                      <div onClick={() => setCtlExpanded(p => ({ ...p, [ctl.id]: !isOpen }))}
                        style={{ display:'flex', alignItems:'center', gap:'12px', padding:'14px 16px', cursor:'pointer' }}>
                        <div style={{ width:'10px', height:'10px', borderRadius:'50%', background: ctl.status === 'online' ? '#22c55e' : '#ef4444', flexShrink:0 }} />
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:'13px', fontWeight:600, color:'#e2e8f0' }}>{ctl.name}</div>
                          <div style={{ fontSize:'10px', color:'#475569', marginTop:'2px' }}>
                            {ctl.site && <span>{ctl.site} · </span>}
                            {ctl.ip && <span style={{ fontFamily:'monospace' }}>{ctl.ip} · </span>}
                            {doors.length} door{doors.length !== 1 ? 's' : ''}{ctl.firmware && ` · ${ctl.firmware}`}
                          </div>
                        </div>
                        <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
                          {ctl.health !== 'OK' && <span style={{ fontSize:'10px', color:'#f59e0b', fontWeight:600 }}>{ctl.health}</span>}
                          <span style={{ fontSize:'10px', color:'#475569' }}>{ctl.lastSeen}</span>
                          <span style={{ fontSize:'11px', color:'#475569' }}>{isOpen ? '▲' : '▼'}</span>
                        </div>
                      </div>
                      {isOpen && (
                        <div style={{ borderTop:'1px solid rgba(255,255,255,0.05)', background:'rgba(0,0,0,0.15)' }}>
                          {doors.length === 0 ? (
                            <div style={{ padding:'12px 16px', fontSize:'11px', color:'#475569' }}>No doors registered on this controller.</div>
                          ) : doors.map(door => (
                            <div key={door.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 16px', borderBottom:'1px solid rgba(255,255,255,0.03)' }}>
                              <span style={{ fontSize:'14px' }}>🚪</span>
                              <div style={{ flex:1 }}>
                                <div style={{ fontSize:'12px', color:'#e2e8f0', fontWeight:500 }}>{door.name}</div>
                                {door.lastEvent && <div style={{ fontSize:'10px', color:'#475569', marginTop:'1px' }}>{door.lastEvent}</div>}
                              </div>
                              <div style={{ fontSize:'10px', fontWeight:700, padding:'2px 8px', borderRadius:'6px', background:`${doorStatusColor(door.status)}18`, color:doorStatusColor(door.status), textTransform:'capitalize' }}>
                                {door.status}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
                {filteredCtl.length === 0 && (
                  <div style={{ padding:'40px', textAlign:'center', color:'#475569', fontSize:'13px' }}>No controllers found for this site.</div>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── System Tab ───────────────────────────────────────────────── */}
        {/* ── Alarm Notifications Tab ─────────────────────────────────── */}
        {tab === 'alarm-notifications' && (
          <div style={{ padding:'20px' }}>
            <div style={{ fontSize:13, fontWeight:700, color:'#e2e8f0', marginBottom:4 }}>Alarm Notification Settings</div>
            <div style={{ fontSize:12, color:'#64748b', marginBottom:20 }}>
              Configure how each alarm type is displayed. Life-safety alarms (fire, duress) are always shown as full-screen pop-ups. Other alarms default to banner notifications but can be upgraded to pop-ups below.
            </div>
            <div style={{ background:'rgba(245,158,11,0.06)', border:'1px solid rgba(245,158,11,0.2)', borderRadius:8, padding:'10px 14px', marginBottom:20, fontSize:12, color:'#fbbf24' }}>
              ⚡ <strong>Pop-up alarms</strong> appear center-screen with the associated camera feed — operators cannot miss them.<br/>
              📌 <strong>Banner alarms</strong> slide in from the top-right and auto-stack. Best for lower-priority notifications.
            </div>
            <AlarmSettingsPanel />
          </div>
        )}

        {tab === 'system' && isSuperAdmin && (
          <div style={{ padding:'20px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'16px' }}>
              {/* Platform info */}
              <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', padding:'16px' }}>
                <h4 style={{ margin:'0 0 12px', fontSize:'12px', fontWeight:700, color:'#e2e8f0' }}>Platform Info</h4>
                {[
                  ['Version',      'Lemai v2.4.1'],
                  ['Build',        '20260410'],
                  ['Environment',  'Production'],
                  ['Server',       'DigitalOcean — SYD1'],
                  ['Database',     'PostgreSQL 15.2'],
                  ['VMS',          'Milestone XProtect 2024 R1'],
                  ['ACS',         'Protege GX 4.1.5'],
                ].map(([k, v]) => (
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:'11px' }}>
                    <span style={{ color:'#64748b' }}>{k}</span>
                    <span style={{ color:'#e2e8f0', fontWeight:500 }}>{v}</span>
                  </div>
                ))}
              </div>

              {/* Notifications */}
              <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', padding:'16px' }}>
                <h4 style={{ margin:'0 0 12px', fontSize:'12px', fontWeight:700, color:'#e2e8f0' }}>Notifications</h4>
                {[
                  ['Critical Alarm Alerts', true],
                  ['Daily Summary Email',   true],
                  ['Guard Tour Missed',     true],
                  ['Camera Offline Alert',  false],
                  ['Integration Errors',    true],
                  ['Weekly Report',         false],
                ].map(([label, on]) => (
                  <div key={label as string} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                    <span style={{ fontSize:'11px', color:'#94a3b8' }}>{label}</span>
                    <div style={{ width:'32px', height:'18px', borderRadius:'9px', background: on ? '#3b82f6' : 'rgba(100,116,139,0.3)', display:'flex', alignItems:'center', padding:'2px', cursor:'pointer', justifyContent: on ? 'flex-end' : 'flex-start' }}>
                      <div style={{ width:'14px', height:'14px', borderRadius:'50%', background:'white' }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Data retention */}
              <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', padding:'16px' }}>
                <h4 style={{ margin:'0 0 12px', fontSize:'12px', fontWeight:700, color:'#e2e8f0' }}>Data Retention</h4>
                {[
                  ['Video recordings',    '30 days'],
                  ['Access event logs',   '365 days'],
                  ['Alarm history',       '365 days'],
                  ['Guard tour records',  '180 days'],
                  ['Audit trail',         '7 years'],
                  ['Report archive',      '3 years'],
                ].map(([k, v]) => (
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:'11px' }}>
                    <span style={{ color:'#64748b' }}>{k}</span>
                    <span style={{ color:'#e2e8f0', fontWeight:500 }}>{v}</span>
                  </div>
                ))}
                <button style={{ marginTop:'12px', width:'100%', padding:'7px', borderRadius:'7px', border:'1px solid rgba(59,130,246,0.2)', background:'rgba(59,130,246,0.08)', color:'#3b82f6', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                  Edit Retention Policy
                </button>
              </div>

              {/* Security */}
              <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'10px', padding:'16px' }}>
                <h4 style={{ margin:'0 0 12px', fontSize:'12px', fontWeight:700, color:'#e2e8f0' }}>Security</h4>
                {[
                  ['Session Timeout',    '8 hours'],
                  ['MFA Required',       'Yes'],
                  ['IP Whitelist',       'Disabled'],
                  ['Audit Logging',      'Enabled'],
                  ['Last Security Scan', '8 Apr 2026'],
                  ['SSL Certificate',    'Valid — expires Jan 2027'],
                ].map(([k, v]) => (
                  <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:'11px' }}>
                    <span style={{ color:'#64748b' }}>{k}</span>
                    <span style={{ color:'#e2e8f0', fontWeight:500 }}>{v}</span>
                  </div>
                ))}
                <button style={{ marginTop:'12px', width:'100%', padding:'7px', borderRadius:'7px', border:'1px solid rgba(59,130,246,0.2)', background:'rgba(59,130,246,0.08)', color:'#3b82f6', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                  Security Settings
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Camera Linking Tab (Alarm Zones → Camera Assignment) ────── */}
        {tab === 'camera-linking' && (() => {
          const allZoneSites = ['All', ...Array.from(new Set(armorZones.map(z => z.site)))];
          const filteredZones = zoneFilter === 'All' ? armorZones : armorZones.filter(z => z.site === zoneFilter);
          const selKey = selectedZoneKey;
          const selZone = selKey ? armorZones.find(z => `${z.site}|${z.location}` === selKey) : null;
          const selLink = selKey ? getZoneLink(selKey) : null;
          const totalLinked = selLink ? selLink.cameras.length + selLink.manualCameras.length : 0;
          const availableCams = cameras.filter(c => !selLink?.cameras.includes(c.name) && !selLink?.cameras.includes(c.cameraId));

          return (
            <div style={{ display:'flex', height:'calc(100vh - 340px)', minHeight:'460px' }}>
              {/* Left: Zone list */}
              <div style={{ width:'38%', borderRight:'1px solid rgba(255,255,255,0.06)', display:'flex', flexDirection:'column' }}>
                <div style={{ padding:'10px 14px', borderBottom:'1px solid rgba(255,255,255,0.05)', display:'flex', alignItems:'center', gap:'8px', flexShrink:0 }}>
                  <select value={zoneFilter} onChange={e => setZoneFilter(e.target.value)}
                    style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'4px 8px', color:'#e2e8f0', fontSize:'11px', cursor:'pointer', flex:1 }}>
                    {allZoneSites.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => setAddingManualZone(v => !v)}
                    style={{ padding:'4px 10px', borderRadius:'6px', border:'1px solid rgba(255,255,255,0.1)', background:'rgba(255,255,255,0.04)', color:'#64748b', fontSize:'10px', fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                    + Zone
                  </button>
                </div>
                {addingManualZone && (
                  <div style={{ padding:'10px 14px', borderBottom:'1px solid rgba(255,255,255,0.06)', background:'rgba(59,130,246,0.04)', flexShrink:0 }}>
                    <div style={{ fontSize:'10px', color:'#3b82f6', fontWeight:700, marginBottom:'8px', textTransform:'uppercase' }}>Add Manual Zone</div>
                    <select value={newZoneSite} onChange={e => setNewZoneSite(e.target.value)}
                      style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'5px 8px', color:'#e2e8f0', fontSize:'11px', marginBottom:'6px', cursor:'pointer' }}>
                      {SITES_LIST.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input value={newZoneLoc} onChange={e => setNewZoneLoc(e.target.value)} placeholder="Zone / location name"
                      style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'5px 8px', color:'#e2e8f0', fontSize:'11px', marginBottom:'6px', outline:'none', boxSizing:'border-box' as const }} />
                    <button onClick={() => {
                      if (!newZoneLoc.trim()) return;
                      const z: ArmorZone = { site: newZoneSite, location: newZoneLoc.trim(), alarmType: 'Manual', count: 0 };
                      setArmorZones(prev => [...prev, z]);
                      setNewZoneLoc(''); setAddingManualZone(false);
                    }} style={{ padding:'5px 14px', borderRadius:'6px', border:'none', background:'#3b82f6', color:'white', fontSize:'11px', fontWeight:700, cursor:'pointer' }}>Add</button>
                  </div>
                )}
                <div style={{ overflowY:'auto', flex:1 }}>
                  {filteredZones.length === 0 && (
                    <div style={{ padding:'24px', textAlign:'center', color:'#475569', fontSize:'12px' }}>
                      {armorZones.length === 0 ? 'No alarm zones received yet. Zones appear automatically as alarms come in from ArmorIP.' : 'No zones for this site.'}
                    </div>
                  )}
                  {filteredZones.map(z => {
                    const key = `${z.site}|${z.location}`;
                    const link = getZoneLink(key);
                    const count = link.cameras.length + link.manualCameras.length;
                    const isSelected = key === selKey;
                    return (
                      <div key={key} onClick={() => { setSelectedZoneKey(key); setShowCamPicker(false); setShowManualEntry(false); }}
                        style={{ padding:'11px 14px', borderBottom:'1px solid rgba(255,255,255,0.04)', cursor:'pointer', background: isSelected ? 'rgba(59,130,246,0.08)' : 'transparent', borderLeft: isSelected ? '3px solid #3b82f6' : '3px solid transparent' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'2px' }}>
                          <span style={{ fontSize:'12px', fontWeight:600, color:'#e2e8f0', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{z.location}</span>
                          <span style={{ fontSize:'10px', color: count > 0 ? '#22c55e' : '#475569', fontWeight:700, flexShrink:0 }}>{count}/4</span>
                        </div>
                        <div style={{ fontSize:'10px', color:'#475569' }}>
                          {z.site}{z.alarmType !== 'Manual' && ` · ${z.alarmType}`}{z.count > 0 && ` · ${z.count} alarm${z.count !== 1 ? 's' : ''}`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right: Camera assignment panel */}
              <div style={{ flex:1, padding:'20px', overflowY:'auto' }}>
                {!selKey || !selZone ? (
                  <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', height:'100%', color:'#334155', gap:'8px' }}>
                    <div style={{ fontSize:'32px' }}>📹</div>
                    <div style={{ fontSize:'13px' }}>Select an alarm zone to assign cameras</div>
                    <div style={{ fontSize:'11px', color:'#1e293b' }}>Zones populate automatically as alarms arrive from ArmorIP</div>
                  </div>
                ) : (
                  <>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'16px' }}>
                      <div>
                        <div style={{ fontWeight:700, fontSize:'15px', color:'white', marginBottom:'3px' }}>{selZone.location}</div>
                        <div style={{ fontSize:'12px', color:'#64748b' }}>{selZone.site}{selZone.alarmType !== 'Manual' && ` · ${selZone.alarmType}`}</div>
                      </div>
                      <div style={{ fontSize:'11px', fontWeight:700, color: totalLinked >= 4 ? '#f59e0b' : '#22c55e', background: totalLinked >= 4 ? 'rgba(245,158,11,0.1)' : 'rgba(34,197,94,0.08)', padding:'3px 10px', borderRadius:'20px', border:`1px solid ${totalLinked >= 4 ? 'rgba(245,158,11,0.25)' : 'rgba(34,197,94,0.2)'}` }}>
                        {totalLinked}/4 cameras
                      </div>
                    </div>

                    {/* Linked registry cameras */}
                    {selLink!.cameras.map(camName => {
                      const cam = cameras.find(c => c.name === camName || c.cameraId === camName);
                      return (
                        <div key={camName} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'8px', marginBottom:'6px' }}>
                          <span style={{ fontSize:'14px' }}>📹</span>
                          <div style={{ flex:1 }}>
                            <div style={{ fontSize:'12px', color:'#e2e8f0', fontWeight:500 }}>{camName}</div>
                            {cam && <div style={{ fontSize:'10px', color:'#475569' }}>{cam.site} · {cam.location} · {cam.type}</div>}
                          </div>
                          <button onClick={() => removeCamFromZone(selKey!, camName)}
                            style={{ background:'rgba(239,68,68,0.12)', border:'none', borderRadius:'6px', color:'#f87171', fontSize:'10px', padding:'3px 8px', cursor:'pointer' }}>Remove</button>
                        </div>
                      );
                    })}

                    {/* Linked manual cameras */}
                    {selLink!.manualCameras.map((mc, idx) => (
                      <div key={idx} style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:'8px', marginBottom:'6px' }}>
                        <span style={{ fontSize:'14px' }}>📹</span>
                        <div style={{ flex:1 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                            <span style={{ fontSize:'12px', color:'#e2e8f0', fontWeight:500 }}>{mc.name}</span>
                            <span style={{ fontSize:'9px', color:'#f59e0b', background:'rgba(245,158,11,0.1)', padding:'1px 5px', borderRadius:'4px', fontWeight:700 }}>Manual</span>
                          </div>
                          {mc.description && <div style={{ fontSize:'10px', color:'#475569' }}>{mc.description}</div>}
                        </div>
                        <button onClick={() => removeManualCamFromZone(selKey!, idx)}
                          style={{ background:'rgba(239,68,68,0.12)', border:'none', borderRadius:'6px', color:'#f87171', fontSize:'10px', padding:'3px 8px', cursor:'pointer' }}>Remove</button>
                      </div>
                    ))}

                    {totalLinked === 0 && (
                      <div style={{ padding:'14px', background:'rgba(255,255,255,0.02)', borderRadius:'8px', color:'#475569', fontSize:'12px', marginBottom:'12px' }}>No cameras linked yet — add up to 4.</div>
                    )}

                    {/* Add camera from registry */}
                    {totalLinked < 4 && (
                      <div style={{ marginTop:'12px' }}>
                        <button onClick={() => { setShowCamPicker(v => !v); setShowManualEntry(false); }}
                          style={{ padding:'7px 16px', borderRadius:'8px', border:'1px solid rgba(59,130,246,0.3)', background:'rgba(59,130,246,0.08)', color:'#3b82f6', fontSize:'12px', fontWeight:700, cursor:'pointer', marginRight:'8px' }}>
                          + Add from Registry
                        </button>
                        <button onClick={() => { setShowManualEntry(v => !v); setShowCamPicker(false); }}
                          style={{ padding:'7px 16px', borderRadius:'8px', border:'1px solid rgba(255,255,255,0.12)', background:'rgba(255,255,255,0.04)', color:'#94a3b8', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>
                          + Enter Manually
                        </button>
                      </div>
                    )}

                    {/* Camera picker dropdown */}
                    {showCamPicker && totalLinked < 4 && (
                      <div style={{ marginTop:'10px', background:'rgba(13,19,33,0.98)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'10px', overflow:'hidden', maxHeight:'240px', overflowY:'auto' }}>
                        <div style={{ padding:'8px 12px', borderBottom:'1px solid rgba(255,255,255,0.06)', fontSize:'10px', color:'#64748b', fontWeight:600, textTransform:'uppercase' }}>Camera Registry</div>
                        {availableCams.length === 0 ? (
                          <div style={{ padding:'14px', fontSize:'11px', color:'#475569' }}>All cameras already linked or no cameras in registry.</div>
                        ) : availableCams.map(cam => (
                          <div key={cam.id} onClick={() => { addCamToZone(selKey!, cam.name); setShowCamPicker(false); }}
                            style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 12px', cursor:'pointer', borderBottom:'1px solid rgba(255,255,255,0.04)' }}
                            onMouseEnter={e => (e.currentTarget as HTMLElement).style.background = 'rgba(59,130,246,0.08)'}
                            onMouseLeave={e => (e.currentTarget as HTMLElement).style.background = 'transparent'}>
                            <div style={{ width:'8px', height:'8px', borderRadius:'50%', background: cam.status === 'Online' ? '#22c55e' : '#ef4444', flexShrink:0 }} />
                            <div style={{ flex:1 }}>
                              <div style={{ fontSize:'12px', color:'#e2e8f0', fontWeight:500 }}>{cam.name}</div>
                              <div style={{ fontSize:'10px', color:'#475569' }}>{cam.site} · {cam.location} · {cam.type}</div>
                            </div>
                            <span style={{ fontSize:'10px', color:'#3b82f6' }}>Add</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Manual camera entry */}
                    {showManualEntry && totalLinked < 4 && (
                      <div style={{ marginTop:'10px', background:'rgba(245,158,11,0.04)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:'10px', padding:'14px' }}>
                        <div style={{ fontSize:'11px', color:'#f59e0b', fontWeight:700, marginBottom:'10px', textTransform:'uppercase', letterSpacing:'0.5px' }}>Manual Camera Entry</div>
                        <input value={manualCamName} onChange={e => setManualCamName(e.target.value)} placeholder="Camera name (e.g. Stairwell Axis Q3)"
                          style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'7px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none', marginBottom:'6px', boxSizing:'border-box' as const }} />
                        <input value={manualCamDesc} onChange={e => setManualCamDesc(e.target.value)} placeholder="Description (optional — model, location note)"
                          style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', padding:'7px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none', marginBottom:'8px', boxSizing:'border-box' as const }} />
                        <button onClick={() => addManualCamToZone(selKey!)}
                          style={{ padding:'7px 18px', borderRadius:'7px', border:'none', background:'rgba(245,158,11,0.2)', color:'#f59e0b', fontSize:'12px', fontWeight:700, cursor:'pointer' }}>Add Camera</button>
                      </div>
                    )}

                    <div style={{ marginTop:'16px', padding:'12px 14px', background:'rgba(59,130,246,0.05)', border:'1px solid rgba(59,130,246,0.12)', borderRadius:'8px', fontSize:'11px', color:'#64748b', lineHeight:1.6 }}>
                      Linked cameras appear automatically in the SOC alarm popup when this zone triggers. Cameras from the registry show live feeds; manual entries are shown as labels with description.
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── SOP Manager Tab ──────────────────────────────────────────── */}
        {tab === 'sop-manager' && (
          <div style={{ display:'flex', height:'calc(100vh - 340px)', minHeight:'420px' }}>
            {/* Alarm type list */}
            <div style={{ width:'35%', borderRight:'1px solid rgba(255,255,255,0.06)', overflowY:'auto' }}>
              <div style={{ padding:'12px 16px', borderBottom:'1px solid rgba(255,255,255,0.05)', fontSize:'11px', color:'#64748b', fontWeight:600, letterSpacing:'0.5px' }}>ALARM TYPES</div>
              {SOP_ALARM_TYPES.map(t => (
                <div key={t} onClick={() => { setSelectedAlarmType(t); setSopDraft([...(sops[t] || [])]); setSopSaved(false); }} style={{ padding:'11px 16px', borderBottom:'1px solid rgba(255,255,255,0.04)', cursor:'pointer', background: selectedAlarmType === t ? 'rgba(59,130,246,0.08)' : 'transparent', borderLeft: selectedAlarmType === t ? '3px solid #3b82f6' : '3px solid transparent', display:'flex', alignItems:'center', gap:'10px' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:'12px', fontWeight:600, color:'#e2e8f0' }}>{t}</div>
                    <div style={{ fontSize:'10px', color:'#475569', marginTop:'2px' }}>{(sops[t] || []).length} steps</div>
                  </div>
                  {selectedAlarmType === t && <span style={{ color:'#3b82f6', fontSize:'12px' }}>›</span>}
                </div>
              ))}
            </div>
            {/* SOP editor */}
            <div style={{ flex:1, padding:'20px', overflowY:'auto' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:'15px', color:'white' }}>{selectedAlarmType}</div>
                  <div style={{ fontSize:'12px', color:'#64748b', marginTop:'2px' }}>SOC Standard Operating Procedure — editable by admin</div>
                </div>
                {sopSaved && <div style={{ fontSize:'12px', color:'#22c55e', fontWeight:600 }}>✓ Saved</div>}
                <button onClick={() => {
                  setSops(prev => ({ ...prev, [selectedAlarmType]: [...sopDraft] }));
                  setSopSaved(true);
                  setTimeout(() => setSopSaved(false), 3000);
                }} style={{ padding:'8px 20px', background:'#3b82f6', border:'none', borderRadius:'8px', color:'white', fontSize:'13px', fontWeight:700, cursor:'pointer' }}>Save SOP</button>
              </div>
              {sopDraft.map((step, i) => (
                <div key={i} style={{ display:'flex', gap:'8px', marginBottom:'8px', alignItems:'flex-start' }}>
                  <div style={{ width:'24px', height:'24px', borderRadius:'50%', background:'rgba(59,130,246,0.15)', border:'1px solid rgba(59,130,246,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', color:'#93c5fd', fontWeight:700, flexShrink:0, marginTop:'6px' }}>{i + 1}</div>
                  <textarea value={step} onChange={e => { const d = [...sopDraft]; d[i] = e.target.value; setSopDraft(d); setSopSaved(false); }} style={{ flex:1, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'8px', padding:'8px 10px', color:'#e2e8f0', fontSize:'12px', resize:'none', height:'52px', fontFamily:'inherit' }} />
                  <div style={{ display:'flex', flexDirection:'column', gap:'4px', flexShrink:0 }}>
                    <button onClick={() => { if (i === 0) return; const d = [...sopDraft]; const tmp = d[i-1]; d[i-1] = d[i]; d[i] = tmp; setSopDraft(d); }} style={{ padding:'4px 8px', background:'rgba(255,255,255,0.05)', border:'none', borderRadius:'6px', color:'#64748b', cursor:'pointer', fontSize:'12px' }}>↑</button>
                    <button onClick={() => { if (i === sopDraft.length-1) return; const d = [...sopDraft]; const tmp = d[i+1]; d[i+1] = d[i]; d[i] = tmp; setSopDraft(d); }} style={{ padding:'4px 8px', background:'rgba(255,255,255,0.05)', border:'none', borderRadius:'6px', color:'#64748b', cursor:'pointer', fontSize:'12px' }}>↓</button>
                    <button onClick={() => setSopDraft(sopDraft.filter((_, j) => j !== i))} style={{ padding:'4px 8px', background:'rgba(239,68,68,0.1)', border:'none', borderRadius:'6px', color:'#f87171', cursor:'pointer', fontSize:'12px' }}>✕</button>
                  </div>
                </div>
              ))}
              <button onClick={() => setSopDraft(sopDraft.concat(''))} style={{ marginTop:'8px', width:'100%', padding:'9px', background:'rgba(255,255,255,0.04)', border:'1px dashed rgba(255,255,255,0.12)', borderRadius:'8px', color:'#64748b', fontSize:'12px', cursor:'pointer' }}>
                + Add Step
              </button>
              <div style={{ marginTop:'16px', padding:'12px', background:'rgba(245,158,11,0.05)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:'10px', fontSize:'11px', color:'#92400e' }}>
                ⚠️ Changes are applied immediately when you click Save SOP. SOC operators will see the updated steps in their alarm popup checklist.
              </div>
            </div>
          </div>
        )}

        {/* ── Alarm Types Tab ──────────────────────────────────────────── */}
        {tab === 'alarm-types' && (
          <div style={{ display:'flex', height:'calc(100vh - 340px)', minHeight:'500px' }}>
            {/* Left: alarm type list */}
            <div style={{ width:'35%', borderRight:'1px solid rgba(255,255,255,0.06)', overflowY:'auto', display:'flex', flexDirection:'column' as const }}>
              {/* Header with unmapped badge */}
              {(() => {
                const allAliases = new Set(Object.values(alarmTypesConfig).flatMap(c => c.aliases || []).map(a => a.toLowerCase()));
                const unmappedCount = seenTypes.filter(st => !st.mappedTo && !allAliases.has(st.raw.toLowerCase())).length;
                return (
                  <div style={{ padding:'12px 16px', borderBottom:'1px solid rgba(255,255,255,0.05)', display:'flex', alignItems:'center', gap:'8px' }}>
                    <span style={{ fontSize:'11px', color:'#64748b', fontWeight:600, letterSpacing:'0.5px', flex:1 }}>ALARM TYPES</span>
                    {unmappedCount > 0 && (
                      <span style={{ fontSize:'9px', fontWeight:700, padding:'2px 8px', borderRadius:'6px', background:'rgba(245,158,11,0.15)', color:'#f59e0b', border:'1px solid rgba(245,158,11,0.3)' }}>
                        {unmappedCount} UNMAPPED
                      </span>
                    )}
                  </div>
                );
              })()}
              {SOP_ALARM_TYPES.map(t => {
                const cfg = alarmTypesConfig[t];
                const cat = cfg?.category;
                const aliasCount = cfg?.aliases?.length || 0;
                const catColor: Record<string,string> = { Security:'#ef4444', Technical:'#f59e0b', Fire:'#f97316', Medical:'#22c55e', Access:'#3b82f6', Environmental:'#06b6d4', Other:'#64748b' };
                return (
                  <div key={t} onClick={() => {
                    setSelectedTypeForEdit(t);
                    const existing = alarmTypesConfig[t];
                    setAlarmTypeDraft({
                      displayName: existing?.displayName || t,
                      category:    existing?.category || 'Security',
                      sop:         existing?.sop?.length ? [...existing.sop] : [...(DEFAULT_SOPS[t] || [])],
                      aliases:     existing?.aliases ? [...existing.aliases] : [],
                    });
                    setAlarmTypeSaved(false);
                    setAlarmAliasInput('');
                  }} style={{ padding:'11px 16px', borderBottom:'1px solid rgba(255,255,255,0.04)', cursor:'pointer', background: selectedTypeForEdit === t ? 'rgba(59,130,246,0.08)' : 'transparent', borderLeft: selectedTypeForEdit === t ? '3px solid #3b82f6' : '3px solid transparent' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:'12px', fontWeight:600, color:'#e2e8f0' }}>{cfg?.displayName || t}</div>
                        {cfg?.displayName && cfg.displayName !== t && (
                          <div style={{ fontSize:'10px', color:'#475569', marginTop:'1px', fontStyle:'italic' }}>{t}</div>
                        )}
                        {aliasCount > 0 && (
                          <div style={{ fontSize:'10px', color:'#3b82f6', marginTop:'2px' }}>{aliasCount} mapping{aliasCount !== 1 ? 's' : ''}</div>
                        )}
                      </div>
                      {cat && <span style={{ fontSize:'9px', fontWeight:700, padding:'2px 7px', borderRadius:'6px', background:`${catColor[cat] || '#64748b'}20`, color: catColor[cat] || '#64748b', border:`1px solid ${catColor[cat] || '#64748b'}40` }}>{cat.toUpperCase()}</span>}
                      {selectedTypeForEdit === t && <span style={{ color:'#3b82f6', fontSize:'12px' }}>›</span>}
                    </div>
                  </div>
                );
              })}

              {/* Unmapped incoming types section at the bottom of the list */}
              {(() => {
                const allAliases = new Set(Object.values(alarmTypesConfig).flatMap(c => c.aliases || []).map(a => a.toLowerCase()));
                const unmapped = seenTypes.filter(st => !st.mappedTo && !allAliases.has(st.raw.toLowerCase()));
                if (unmapped.length === 0) return null;
                return (
                  <div style={{ borderTop:'1px solid rgba(245,158,11,0.2)', marginTop:'auto' }}>
                    <div style={{ padding:'10px 16px 6px', fontSize:'10px', color:'#f59e0b', fontWeight:600, letterSpacing:'0.4px' }}>UNMAPPED INCOMING TYPES</div>
                    {unmapped.map(st => (
                      <div key={st.raw} style={{ padding:'8px 16px', borderBottom:'1px solid rgba(255,255,255,0.03)', display:'flex', alignItems:'center', gap:'8px' }}>
                        <span style={{ fontSize:'11px', fontFamily:'monospace', color:'#94a3b8', flex:1, wordBreak:'break-all' as const }}>{st.raw}</span>
                        <span style={{ fontSize:'9px', color:'#f59e0b', fontWeight:600, flexShrink:0 }}>UNMAP</span>
                      </div>
                    ))}
                    <div style={{ padding:'8px 16px 12px', fontSize:'10px', color:'#475569' }}>
                      Select a type above, then click an unmapped string to map it.
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Right: editor */}
            <div style={{ flex:1, padding:'20px', overflowY:'auto' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'20px' }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700, fontSize:'15px', color:'white' }}>{selectedTypeForEdit}</div>
                  <div style={{ fontSize:'12px', color:'#64748b', marginTop:'2px' }}>Customise how this alarm type appears and what operators see</div>
                </div>
                {alarmTypeSaved && <div style={{ fontSize:'12px', color:'#22c55e', fontWeight:600 }}>✓ Saved</div>}
                <button onClick={async () => {
                  const updated = { ...alarmTypesConfig, [selectedTypeForEdit]: { ...alarmTypeDraft } };
                  await saveAlarmTypesConfig(updated);
                  setAlarmTypeSaved(true);
                  setTimeout(() => setAlarmTypeSaved(false), 3000);
                }} style={{ padding:'8px 20px', background:'#3b82f6', border:'none', borderRadius:'8px', color:'white', fontSize:'13px', fontWeight:700, cursor:'pointer' }}>Save</button>
              </div>

              {/* Display Name */}
              <div style={{ marginBottom:'16px' }}>
                <label style={{ display:'block', fontSize:'11px', color:'#64748b', marginBottom:'6px', fontWeight:600, letterSpacing:'0.4px' }}>DISPLAY NAME</label>
                <input
                  value={alarmTypeDraft.displayName}
                  onChange={e => setAlarmTypeDraft(d => ({ ...d, displayName: e.target.value }))}
                  placeholder={selectedTypeForEdit}
                  style={{ width:'100%', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'9px 12px', color:'#e2e8f0', fontSize:'13px', outline:'none', boxSizing:'border-box' as const }}
                />
                <div style={{ fontSize:'10px', color:'#475569', marginTop:'4px' }}>Shown on alarm rows in SOC Dashboard instead of the raw internal type string</div>
              </div>

              {/* Category */}
              <div style={{ marginBottom:'20px' }}>
                <label style={{ display:'block', fontSize:'11px', color:'#64748b', marginBottom:'6px', fontWeight:600, letterSpacing:'0.4px' }}>CATEGORY</label>
                <div style={{ display:'flex', flexWrap:'wrap' as const, gap:'8px' }}>
                  {ALARM_CATEGORIES.map(cat => {
                    const catColor: Record<string,string> = { Security:'#ef4444', Technical:'#f59e0b', Fire:'#f97316', Medical:'#22c55e', Access:'#3b82f6', Environmental:'#06b6d4', Other:'#64748b' };
                    const active = alarmTypeDraft.category === cat;
                    return (
                      <button key={cat} onClick={() => setAlarmTypeDraft(d => ({ ...d, category: cat }))} style={{
                        padding:'5px 14px', borderRadius:'20px', border:`1px solid ${catColor[cat] || '#64748b'}${active ? 'ff' : '50'}`,
                        background: active ? `${catColor[cat] || '#64748b'}25` : 'transparent',
                        color: active ? (catColor[cat] || '#64748b') : '#64748b', fontSize:'11px', fontWeight:600, cursor:'pointer', transition:'all 0.15s',
                      }}>{cat}</button>
                    );
                  })}
                </div>
                <div style={{ fontSize:'10px', color:'#475569', marginTop:'4px' }}>Shown as a badge on alarm rows in SOC Dashboard</div>
              </div>

              {/* SOP steps */}
              <div>
                <label style={{ display:'block', fontSize:'11px', color:'#64748b', marginBottom:'8px', fontWeight:600, letterSpacing:'0.4px' }}>SOP CHECKLIST STEPS</label>
                {alarmTypeDraft.sop.map((step, i) => (
                  <div key={i} style={{ display:'flex', gap:'8px', marginBottom:'8px', alignItems:'flex-start' }}>
                    <div style={{ width:'24px', height:'24px', borderRadius:'50%', background:'rgba(59,130,246,0.15)', border:'1px solid rgba(59,130,246,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', color:'#93c5fd', fontWeight:700, flexShrink:0, marginTop:'6px' }}>{i + 1}</div>
                    <textarea value={step} onChange={e => { const d = [...alarmTypeDraft.sop]; d[i] = e.target.value; setAlarmTypeDraft(prev => ({ ...prev, sop: d })); }} style={{ flex:1, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'8px', padding:'8px 10px', color:'#e2e8f0', fontSize:'12px', resize:'none', height:'52px', fontFamily:'inherit' }} />
                    <div style={{ display:'flex', flexDirection:'column', gap:'4px', flexShrink:0 }}>
                      <button onClick={() => { if (i === 0) return; const d = [...alarmTypeDraft.sop]; [d[i-1],d[i]] = [d[i],d[i-1]]; setAlarmTypeDraft(prev => ({ ...prev, sop: d })); }} style={{ padding:'4px 8px', background:'rgba(255,255,255,0.05)', border:'none', borderRadius:'6px', color:'#64748b', cursor:'pointer', fontSize:'12px' }}>↑</button>
                      <button onClick={() => { if (i === alarmTypeDraft.sop.length-1) return; const d = [...alarmTypeDraft.sop]; [d[i+1],d[i]] = [d[i],d[i+1]]; setAlarmTypeDraft(prev => ({ ...prev, sop: d })); }} style={{ padding:'4px 8px', background:'rgba(255,255,255,0.05)', border:'none', borderRadius:'6px', color:'#64748b', cursor:'pointer', fontSize:'12px' }}>↓</button>
                      <button onClick={() => setAlarmTypeDraft(prev => ({ ...prev, sop: prev.sop.filter((_, j) => j !== i) }))} style={{ padding:'4px 8px', background:'rgba(239,68,68,0.1)', border:'none', borderRadius:'6px', color:'#f87171', cursor:'pointer', fontSize:'12px' }}>✕</button>
                    </div>
                  </div>
                ))}
                <button onClick={() => setAlarmTypeDraft(prev => ({ ...prev, sop: [...prev.sop, ''] }))} style={{ marginTop:'4px', width:'100%', padding:'9px', background:'rgba(255,255,255,0.04)', border:'1px dashed rgba(255,255,255,0.12)', borderRadius:'8px', color:'#64748b', fontSize:'12px', cursor:'pointer' }}>
                  + Add Step
                </button>
                <div style={{ marginTop:'12px', padding:'10px 12px', background:'rgba(59,130,246,0.04)', border:'1px solid rgba(59,130,246,0.12)', borderRadius:'8px', fontSize:'11px', color:'#64748b' }}>
                  💡 These steps override the SOP Manager steps for this alarm type. SOC operators see them in the acknowledge dialog checklist.
                </div>
              </div>

              {/* ── Patriot / Incoming Type Mappings ── */}
              <div style={{ marginTop:'24px', paddingTop:'20px', borderTop:'1px solid rgba(255,255,255,0.06)' }}>
                <label style={{ display:'block', fontSize:'11px', color:'#64748b', marginBottom:'6px', fontWeight:600, letterSpacing:'0.4px' }}>PATRIOT / INCOMING TYPE MAPPINGS</label>
                <div style={{ fontSize:'11px', color:'#475569', marginBottom:'10px' }}>
                  Map raw alarm type strings sent by Patriot (or ArmorIP) to this canonical type. When a matching string arrives, it gets this display name, category and SOP automatically.
                </div>

                {/* Current aliases */}
                {alarmTypeDraft.aliases.length > 0 && (
                  <div style={{ marginBottom:'10px', display:'flex', flexDirection:'column' as const, gap:'6px' }}>
                    {alarmTypeDraft.aliases.map((alias, i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'7px 10px', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'8px' }}>
                        <span style={{ fontSize:'11px', fontFamily:'monospace', color:'#e2e8f0', flex:1 }}>{alias}</span>
                        <button onClick={() => setAlarmTypeDraft(prev => ({ ...prev, aliases: prev.aliases.filter((_, j) => j !== i) }))}
                          style={{ padding:'2px 8px', background:'rgba(239,68,68,0.1)', border:'none', borderRadius:'5px', color:'#f87171', cursor:'pointer', fontSize:'11px' }}>✕</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Manual add */}
                <div style={{ display:'flex', gap:'8px', marginBottom:'12px' }}>
                  <input
                    value={alarmAliasInput}
                    onChange={e => setAlarmAliasInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        const v = alarmAliasInput.trim();
                        if (v && !alarmTypeDraft.aliases.includes(v)) {
                          setAlarmTypeDraft(prev => ({ ...prev, aliases: [...prev.aliases, v] }));
                        }
                        setAlarmAliasInput('');
                      }
                    }}
                    placeholder='Type a Patriot string and press Enter…'
                    style={{ flex:1, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'8px', padding:'8px 10px', color:'#e2e8f0', fontSize:'12px', outline:'none' }}
                  />
                  <button onClick={() => {
                    const v = alarmAliasInput.trim();
                    if (v && !alarmTypeDraft.aliases.includes(v)) {
                      setAlarmTypeDraft(prev => ({ ...prev, aliases: [...prev.aliases, v] }));
                    }
                    setAlarmAliasInput('');
                  }} style={{ padding:'8px 14px', background:'rgba(59,130,246,0.15)', border:'1px solid rgba(59,130,246,0.3)', borderRadius:'8px', color:'#93c5fd', fontSize:'12px', fontWeight:600, cursor:'pointer' }}>Add</button>
                </div>

                {/* Unmapped incoming types from Patriot — quick-add buttons */}
                {(() => {
                  const allAliases = new Set(
                    Object.values(alarmTypesConfig).flatMap(c => c.aliases || []).map(a => a.toLowerCase())
                  );
                  // also include draft aliases
                  alarmTypeDraft.aliases.forEach(a => allAliases.add(a.toLowerCase()));
                  const unmapped = seenTypes.filter(st => !st.mappedTo && !allAliases.has(st.raw.toLowerCase()));
                  if (unmapped.length === 0) return null;
                  return (
                    <div style={{ padding:'10px 12px', background:'rgba(245,158,11,0.04)', border:'1px solid rgba(245,158,11,0.15)', borderRadius:'8px' }}>
                      <div style={{ fontSize:'10px', color:'#f59e0b', fontWeight:600, marginBottom:'8px', letterSpacing:'0.4px' }}>INCOMING UNMAPPED TYPES — click to add as alias</div>
                      <div style={{ display:'flex', flexWrap:'wrap' as const, gap:'6px' }}>
                        {unmapped.map(st => (
                          <button key={st.raw} onClick={() => {
                            if (!alarmTypeDraft.aliases.includes(st.raw)) {
                              setAlarmTypeDraft(prev => ({ ...prev, aliases: [...prev.aliases, st.raw] }));
                            }
                          }} style={{ padding:'3px 10px', background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.3)', borderRadius:'6px', color:'#fbbf24', fontSize:'11px', fontFamily:'monospace', cursor:'pointer' }}>
                            + {st.raw}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* ── Escalation Rules Tab ─────────────────────────────────────── */}
        {tab === 'escalation' && (
          <div style={{ padding:'20px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px' }}>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:'14px', color:'white' }}>Alarm Escalation Rules</div>
                <div style={{ fontSize:'12px', color:'#64748b', marginTop:'2px' }}>Auto-escalate unacknowledged alarms based on severity and timeout</div>
              </div>
              <button style={{ padding:'8px 18px', background:'#3b82f6', border:'none', borderRadius:'8px', color:'white', fontSize:'13px', fontWeight:700, cursor:'pointer' }}>+ Add Rule</button>
            </div>
            {/* Rules table */}
            <div style={{ background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', overflow:'hidden' }}>
              <div style={{ display:'grid', gridTemplateColumns:'100px 180px 90px 1fr 120px 80px', gap:'0', padding:'10px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)', fontSize:'10px', color:'#475569', fontWeight:600, letterSpacing:'0.5px' }}>
                <div>SEVERITY</div><div>ALARM TYPE</div><div>ACK TIMEOUT</div><div>ACTION</div><div>ESCALATE TO</div><div>ENABLED</div>
              </div>
              {escalation.map(rule => {
                const sevColor: Record<string, string> = { critical:'#ef4444', high:'#f59e0b', medium:'#3b82f6', low:'#22c55e' };
                return (
                  <div key={rule.id} style={{ display:'grid', gridTemplateColumns:'100px 180px 90px 1fr 120px 80px', gap:'0', padding:'12px 16px', borderBottom:'1px solid rgba(255,255,255,0.04)', alignItems:'center' }}>
                    <div style={{ fontSize:'11px', fontWeight:700, color:sevColor[rule.severity] || '#94a3b8', textTransform:'uppercase' }}>{rule.severity}</div>
                    <div style={{ fontSize:'12px', color:'#e2e8f0' }}>{rule.alarmType}</div>
                    <div>
                      <input defaultValue={String(rule.ackTimeout)} style={{ width:'50px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'4px 8px', color:'white', fontSize:'12px', textAlign:'center' }} />
                      <span style={{ fontSize:'11px', color:'#475569', marginLeft:'4px' }}>min</span>
                    </div>
                    <div style={{ fontSize:'11px', color:'#94a3b8', paddingRight:'10px' }}>{rule.action}</div>
                    <div style={{ fontSize:'11px', color:'#64748b' }}>{rule.escalateTo}</div>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                      <div onClick={() => setEscalation(prev => prev.map(r => r.id === rule.id ? { ...r, enabled: !r.enabled } : r))} style={{ width:'36px', height:'20px', borderRadius:'10px', background: rule.enabled ? '#22c55e' : '#334155', cursor:'pointer', position:'relative', flexShrink:0 }}>
                        <div style={{ position:'absolute', top:'2px', left: rule.enabled ? '18px' : '2px', width:'16px', height:'16px', borderRadius:'50%', background:'white', transition:'left 0.2s' }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Global Timeout Thresholds */}
            <div style={{ marginTop:'20px', background:'rgba(255,255,255,0.02)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:'12px', padding:'18px 20px' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'14px' }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:'13px', color:'white' }}>Global Escalation Timeouts</div>
                  <div style={{ fontSize:'11px', color:'#64748b', marginTop:'2px' }}>Configure when unacknowledged critical/high alarms escalate through each level</div>
                </div>
                <button
                  onClick={async () => {
                    setEscSaving(true);
                    try {
                      const tok = localStorage.getItem('lemai_token') || '';
                      const res = await fetch('/api/armorip/escalation-config', {
                        method: 'POST',
                        headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ thresholds: escThresholds, enabled: true }),
                      });
                      if (res.ok) { setEscSaved(true); setTimeout(() => setEscSaved(false), 2500); }
                    } finally { setEscSaving(false); }
                  }}
                  style={{ padding:'7px 16px', background: escSaved ? '#22c55e' : '#3b82f6', border:'none', borderRadius:'8px', color:'white', fontSize:'12px', fontWeight:700, cursor:'pointer', minWidth:'80px' }}
                >
                  {escSaving ? 'Saving…' : escSaved ? '✓ Saved' : 'Save'}
                </button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                {escThresholds.map((t, idx) => {
                  const lvlColors = ['#3b82f6','#f59e0b','#f97316','#ef4444'];
                  return (
                    <div key={t.level} style={{ background:'rgba(255,255,255,0.03)', border:`1px solid ${lvlColors[idx]}30`, borderRadius:'10px', padding:'14px' }}>
                      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px' }}>
                        <div style={{ fontSize:'11px', fontWeight:700, color: lvlColors[idx], textTransform:'uppercase', letterSpacing:'0.5px' }}>
                          L{idx + 1}
                        </div>
                        <div style={{ fontSize:'10px', color:'#64748b' }}>{t.description}</div>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                        <input
                          type='number' min='1' max='240'
                          value={t.minutes}
                          onChange={e => setEscThresholds(prev => prev.map((x, i) => i === idx ? { ...x, minutes: Number(e.target.value) } : x))}
                          style={{ width:'60px', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'6px', padding:'5px 8px', color:'white', fontSize:'13px', fontWeight:700, textAlign:'center', outline:'none' }}
                        />
                        <span style={{ fontSize:'11px', color:'#64748b' }}>minutes with no ACK</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{ marginTop:'16px', padding:'14px', background:'rgba(59,130,246,0.05)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'12px', fontSize:'12px', color:'#64748b' }}>
              💡 <strong style={{ color:'#93c5fd' }}>How escalation works:</strong> When an alarm is not acknowledged within the timeout window, the system automatically pages the on-call officer at the matching escalation level and triggers the configured action. All escalations are logged in the audit trail.
            </div>
          </div>
        )}

        {/* ── Security & MFA Tab ─────────────────────────────────────────── */}
        {tab === 'security' && <SecurityMFATab />}

        {/* ── Notifications Tab ──────────────────────────────────────────── */}
        {tab === 'notifications' && <NotificationsTab />}

        {/* ── ArmorIP Webhook Tab ─────────────────────────────────────────── */}
        {tab === 'armorip' && (
          <div style={{ padding:'20px', maxWidth:'720px' }}>
            <div style={{ background:'rgba(168,85,247,0.04)', border:'1px solid rgba(168,85,247,0.15)', borderRadius:'12px', padding:'20px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px' }}>
                <div style={{ width:'36px', height:'36px', borderRadius:'10px', background:'rgba(168,85,247,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'18px' }}>⚡</div>
                <div>
                  <div style={{ fontSize:'14px', fontWeight:700, color:'#e2e8f0' }}>ArmorIP Webhook</div>
                  <div style={{ fontSize:'11px', color:'#64748b' }}>ArmorIP signals are received via this webhook and broadcast live to the SOC Dashboard. To link cameras to alarm zones, use the Camera Linking tab.</div>
                </div>
              </div>
              <div style={{ background:'rgba(0,0,0,0.3)', border:'1px solid rgba(255,255,255,0.06)', borderRadius:'8px', padding:'14px', marginBottom:'16px' }}>
                <div style={{ fontSize:'10px', color:'#64748b', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'8px' }}>Webhook Endpoint — configure this URL in ArmorIP</div>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <code style={{ flex:1, fontSize:'12px', color:'#c084fc', fontFamily:'monospace', background:'rgba(168,85,247,0.06)', padding:'8px 12px', borderRadius:'6px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {armorWebhookUrl || 'https://lemai.ai/api/armorip/webhook'}
                  </code>
                  <button onClick={() => { navigator.clipboard.writeText(armorWebhookUrl); toast('URL copied', 'success'); }}
                    style={{ padding:'6px 12px', borderRadius:'6px', border:'1px solid rgba(168,85,247,0.3)', background:'rgba(168,85,247,0.1)', color:'#c084fc', fontSize:'11px', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>
                    Copy
                  </button>
                </div>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'16px' }}>
                {[
                  { label:'Method', value:'POST' },
                  { label:'Format', value:'JSON' },
                  { label:'Auth',   value:'HMAC-SHA256 (optional)' },
                  { label:'Header', value:'X-ArmorIP-Signature: sha256=...' },
                ].map(r => (
                  <div key={r.label} style={{ background:'rgba(255,255,255,0.03)', borderRadius:'8px', padding:'10px 12px' }}>
                    <div style={{ fontSize:'10px', color:'#475569', fontWeight:600, marginBottom:'3px' }}>{r.label}</div>
                    <div style={{ fontSize:'11px', color:'#94a3b8', fontFamily:'monospace' }}>{r.value}</div>
                  </div>
                ))}
              </div>
              <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:'8px', padding:'12px', marginBottom:'16px', fontSize:'11px', color:'#64748b', lineHeight:'1.6' }}>
                <strong style={{ color:'#94a3b8' }}>Accepted fields:</strong> alarm_type, location, site, severity (1–8 or critical/high/medium/low), camera_id, description, id.
                Field names are normalised — AlarmType, alarmType, event_type all work.
              </div>
              <div style={{ borderTop:'1px solid rgba(255,255,255,0.06)', paddingTop:'16px' }}>
                <div style={{ fontSize:'12px', fontWeight:700, color:'#e2e8f0', marginBottom:'10px' }}>Send Test Alarm to SOC Dashboard</div>
                <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
                  <select value={armorTestType} onChange={e => setArmorTestType(e.target.value)}
                    style={{ flex:1, padding:'7px 10px', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'7px', color:'#e2e8f0', fontSize:'12px', outline:'none' }}>
                    {SOP_ALARM_TYPES.map(t => <option key={t} style={{ background:'#1e293b' }}>{t}</option>)}
                  </select>
                  <button onClick={sendTestAlarm} style={{
                    padding:'7px 20px', borderRadius:'7px', border:'none', fontWeight:700, fontSize:'12px', cursor:'pointer',
                    background: armorTestSent ? 'rgba(34,197,94,0.15)' : 'linear-gradient(135deg,#a855f7,#7c3aed)',
                    color: armorTestSent ? '#22c55e' : 'white', transition:'all 0.2s', whiteSpace:'nowrap',
                  }}>
                    {armorTestSent ? '✓ Sent!' : 'Send Test'}
                  </button>
                </div>
              </div>
            </div>
            <div style={{ marginTop:'16px', padding:'12px 16px', background:'rgba(59,130,246,0.05)', border:'1px solid rgba(59,130,246,0.12)', borderRadius:'10px', fontSize:'12px', color:'#64748b' }}>
              To link cameras to specific alarm zones, go to the <strong style={{ color:'#93c5fd', cursor:'pointer' }} onClick={() => setTab('camera-linking')}>Camera Linking</strong> tab.
            </div>
            {/* ── Suppression Rules ── */}
            <SuppressionPanel />
          </div>
        )}

        {/* ── Active Sessions Tab ─────────────────────────────────────────── */}
        {tab === 'sessions' && <SessionsTab />}

        {/* ── On-Call Schedule Tab ────────────────────────────────────────── */}
        {tab === 'oncall' && <OnCallTab />}

        {/* ── Email / SMTP Tab ────────────────────────────────────────────── */}
        {tab === 'email' && <EmailTab />}

        {/* ── Branding Tab ────────────────────────────────────────────────── */}
        {tab === 'branding' && <BrandingTab />}

      </div>

      {/* Modals */}
      {showAddCam && <AddCameraModal onSave={handleAddCamera} onClose={() => setShowAddCam(false)} />}
      {showAddInt && <AddIntegrationModal onSave={handleAddIntegration} onClose={() => setShowAddInt(false)} />}
    </Layout>
  );
}
